import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useConfig } from '../context/ConfigContext';
import { formatPhoneDisplay, formatPhoneInput, stripPhone } from '../utils/phoneFormat';
import usePullToRefresh from '../hooks/usePullToRefresh';
import { PRODUCT_TYPE } from '../config/product';

// Default expanded sections (Profile expanded, rest collapsed)
const DEFAULT_SECTIONS = {
  profile: true,
  team: false,
  company: false,
  pricing: false,
  invoicing: false,
  operations: false,
  intake: false,
  feedback: false,
};

// Load sections state from localStorage
const loadSectionsState = () => {
  try {
    const saved = localStorage.getItem('lawntrack_settings_sections');
    if (saved) {
      return { ...DEFAULT_SECTIONS, ...JSON.parse(saved) };
    }
  } catch (e) {
    console.error('Failed to load settings sections state:', e);
  }
  return DEFAULT_SECTIONS;
};
function Settings() {
  const navigate = useNavigate();
  const { user, isAdmin, isOwner, isDriver, refreshUser, logout, isSuperAdmin } = useAuth();
  const { getAppName } = useConfig();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Collapsible sections state
  const [expandedSections, setExpandedSections] = useState(loadSectionsState);

  // Toggle section and persist to localStorage
  const toggleSection = useCallback((section) => {
    setExpandedSections(prev => {
      const updated = { ...prev, [section]: !prev[section] };
      try {
        localStorage.setItem('lawntrack_settings_sections', JSON.stringify(updated));
      } catch (e) {
        console.error('Failed to save settings sections state:', e);
      }
      return updated;
    });
  }, []);
  // Profile form state
  const [profile, setProfile] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
  });
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileSuccess, setProfileSuccess] = useState('');
  const [profileError, setProfileError] = useState('');

  // Password form state
  const [passwords, setPasswords] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordSuccess, setPasswordSuccess] = useState('');
  const [passwordError, setPasswordError] = useState('');

  // Company form state (for owners/admins)
  const [company, setCompany] = useState({
    name: '',
    companyDescription: '',
    companyTagline: '',
    showPublicPricing: true,
    businessPhone: '',
    businessEmail: '',
    standardServices: 'mow,blow',
    seasonalDiscountPercent: 0,
    seasonalDiscountAmount: 0,
  });
  const [companyLoading, setCompanyLoading] = useState(false);
  const [companySuccess, setCompanySuccess] = useState('');
  const [companyError, setCompanyError] = useState('');

  // Pricing settings state (for owners/admins)
  const [pricing, setPricing] = useState({
    tier_small_base: '50.00',
    tier_small_salt: '8.00',
    tier_medium_base: '65.00',
    tier_medium_salt: '12.00',
    tier_large_base: '85.00',
    tier_large_salt: '18.00',
    referral_discount: '5.00',
    minimum_price: '35.00',
  });
  const [pricingLoading, setPricingLoading] = useState(false);
  const [pricingSuccess, setPricingSuccess] = useState('');
  const [pricingError, setPricingError] = useState('');

  // Payment methods state
  const [paymentMethods, setPaymentMethods] = useState([
    { id: 'square', name: 'Square', enabled: true, isBuiltIn: true },
    { id: 'venmo', name: 'Venmo', enabled: true, isBuiltIn: true },
    { id: 'cash', name: 'Cash', enabled: true, isBuiltIn: true },
    { id: 'cashapp', name: 'Cash App', enabled: true, isBuiltIn: true },
    { id: 'zelle', name: 'Zelle', enabled: true, isBuiltIn: true },
    { id: 'check', name: 'Check', enabled: true, isBuiltIn: true },
  ]);
  const [paymentMethodsLoading, setPaymentMethodsLoading] = useState(false);
  const [paymentMethodsSuccess, setPaymentMethodsSuccess] = useState('');
  const [paymentMethodsError, setPaymentMethodsError] = useState('');
  const [newMethodName, setNewMethodName] = useState('');

  // Lead sources state
  const [leadSources, setLeadSources] = useState([
    { id: 'facebook', name: 'Facebook', enabled: true, isBuiltIn: true },
    { id: 'craigslist', name: 'Craigslist', enabled: true, isBuiltIn: true },
    { id: 'flyer', name: 'Flyer', enabled: true, isBuiltIn: true },
    { id: 'yard_sign', name: 'Yard Sign', enabled: true, isBuiltIn: true },
    { id: 'referral', name: 'Friend/Referral', enabled: true, isBuiltIn: true },
    { id: 'google', name: 'Google', enabled: true, isBuiltIn: true },
    { id: 'nextdoor', name: 'Nextdoor', enabled: true, isBuiltIn: true },
    { id: 'other', name: 'Other', enabled: true, isBuiltIn: true },
  ]);
  const [leadSourcesLoading, setLeadSourcesLoading] = useState(false);
  const [leadSourcesSuccess, setLeadSourcesSuccess] = useState('');
  const [leadSourcesError, setLeadSourcesError] = useState('');
  const [newSourceName, setNewSourceName] = useState('');

  // Company code for intake form
  const [companyCode, setCompanyCode] = useState('');
  const [copiedLink, setCopiedLink] = useState(false);

  // Custom slug state
  const [customSlug, setCustomSlug] = useState('');
  const [customSlugInput, setCustomSlugInput] = useState('');
  const [slugAvailable, setSlugAvailable] = useState(null); // null = not checked, true/false = available
  const [slugChecking, setSlugChecking] = useState(false);
  const [slugSaving, setSlugSaving] = useState(false);
  const [slugError, setSlugError] = useState('');
  const [slugSuccess, setSlugSuccess] = useState('');
  const [slugCheckTimeout, setSlugCheckTimeout] = useState(null);

  // Lead source stats
  const [leadSourceStats, setLeadSourceStats] = useState({ stats: [], total: 0 });
  const [leadSourceStatsPeriod, setLeadSourceStatsPeriod] = useState('all');
  const [leadSourceStatsLoading, setLeadSourceStatsLoading] = useState(false);

  // Invoice defaults state
  const [invoiceDefaults, setInvoiceDefaults] = useState({
    defaultInvoiceTitle: '{route_name}',
    defaultInvoiceMessage: 'Thank you for your business!',
    autoInvoiceSquare: false,
  });
  const [invoiceDefaultsLoading, setInvoiceDefaultsLoading] = useState(false);
  const [invoiceDefaultsSuccess, setInvoiceDefaultsSuccess] = useState('');
  const [invoiceDefaultsError, setInvoiceDefaultsError] = useState('');

  // Pricing tiers state
  const [pricingTiers, setPricingTiers] = useState([]);
  const [pricingTiersLoading, setPricingTiersLoading] = useState(false);
  const [pricingTiersSuccess, setPricingTiersSuccess] = useState('');
  const [pricingTiersError, setPricingTiersError] = useState('');
  const [tiersModified, setTiersModified] = useState(false);

  // Feature request state
  const [featureRequest, setFeatureRequest] = useState({ subject: '', description: '' });
  const [featureRequestLoading, setFeatureRequestLoading] = useState(false);
  const [featureRequestSuccess, setFeatureRequestSuccess] = useState('');
  const [featureRequestError, setFeatureRequestError] = useState('');

  // My feature requests state
  const [myFeatureRequests, setMyFeatureRequests] = useState([]);
  const [myFeatureRequestsLoading, setMyFeatureRequestsLoading] = useState(false);

  // Weather location state
  const [weatherSettings, setWeatherSettings] = useState({
    weatherUseHome: false,
    weatherCity: '',
    weatherState: '',
  });
  const [weatherSettingsLoading, setWeatherSettingsLoading] = useState(false);
  const [weatherSettingsSuccess, setWeatherSettingsSuccess] = useState('');
  const [weatherSettingsError, setWeatherSettingsError] = useState('');

  // Intake disclaimer state
  const DEFAULT_DISCLAIMER = `Payment for lawn care services is due upon receipt of invoice. In the event of non-payment, {company_name} reserves the right to pursue all available legal remedies, including but not limited to filing a mechanic's lien (labor lien) against the property where services were rendered. If you are a tenant, you acknowledge that a lien may be filed against the property owner's interest in the property.`;

  const [disclaimerSettings, setDisclaimerSettings] = useState({
    enabled: true,
    text: DEFAULT_DISCLAIMER,
  });
  const [disclaimerSettingsLoading, setDisclaimerSettingsLoading] = useState(false);
  const [disclaimerSettingsSuccess, setDisclaimerSettingsSuccess] = useState('');
  const [disclaimerSettingsError, setDisclaimerSettingsError] = useState('');

  // Route optimization state
  const [routeSettings, setRouteSettings] = useState({
    useGps: true,
  });
  const [routeSettingsLoading, setRouteSettingsLoading] = useState(false);
  const [routeSettingsSuccess, setRouteSettingsSuccess] = useState('');
  const [routeSettingsError, setRouteSettingsError] = useState('');

  // Expense categories state
  const [expenseCategories, setExpenseCategories] = useState([]);
  const [expenseCategoriesLoading, setExpenseCategoriesLoading] = useState(false);
  const [expenseCategoriesSuccess, setExpenseCategoriesSuccess] = useState('');
  const [expenseCategoriesError, setExpenseCategoriesError] = useState('');
  const [newCategoryName, setNewCategoryName] = useState('');

  // Service add-ons state
  const [serviceAddons, setServiceAddons] = useState([]);
  const [serviceAddonsLoading, setServiceAddonsLoading] = useState(false);
  const [serviceAddonsSuccess, setServiceAddonsSuccess] = useState('');
  const [serviceAddonsError, setServiceAddonsError] = useState('');
  const [newAddonName, setNewAddonName] = useState('');
  const [newAddonPrice, setNewAddonPrice] = useState('');
  const [addonTierPrices, setAddonTierPrices] = useState({}); // { addonId: { tierId: price } }

  // Team management state
  const [teamMembers, setTeamMembers] = useState([]);
  const [teamInvitations, setTeamInvitations] = useState([]);
  const [teamLoading, setTeamLoading] = useState(false);
  const [teamError, setTeamError] = useState('');
  const [teamSuccess, setTeamSuccess] = useState('');
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('driver');
  const [inviteLoading, setInviteLoading] = useState(false);
  const [inviteError, setInviteError] = useState('');
  const [editingMember, setEditingMember] = useState(null);
  const [confirmRemove, setConfirmRemove] = useState(null);

  // US States list
  const US_STATES = [
    'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
    'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
    'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
    'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
    'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY'
  ];

  const loadMyFeatureRequests = useCallback(async () => {
    setMyFeatureRequestsLoading(true);
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/feature-requests/mine`);
      setMyFeatureRequests(response.data);
    } catch (err) {
      console.error('Failed to load feature requests:', err);
    } finally {
      setMyFeatureRequestsLoading(false);
    }
  }, []);

  // Team management functions
  const loadTeamMembers = useCallback(async () => {
    setTeamLoading(true);
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/team`);
      setTeamMembers(response.data);
    } catch (err) {
      console.error('Failed to load team members:', err);
      setTeamError('Failed to load team members');
    } finally {
      setTeamLoading(false);
    }
  }, []);

  const loadTeamInvitations = useCallback(async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/team/invitations`);
      setTeamInvitations(response.data);
    } catch (err) {
      // Non-admins will get 403, which is expected
      if (err.response?.status !== 403) {
        console.error('Failed to load invitations:', err);
      }
    }
  }, []);

  const handleSendInvite = async (e) => {
    e.preventDefault();
    setInviteError('');
    setInviteLoading(true);

    try {
      await axios.post(`${process.env.REACT_APP_API_URL}/api/team/invite`, {
        email: inviteEmail,
        role: inviteRole,
      });
      setTeamSuccess('Invitation sent successfully!');
      setShowInviteModal(false);
      setInviteEmail('');
      setInviteRole('driver');
      loadTeamInvitations();
      setTimeout(() => setTeamSuccess(''), 3000);
    } catch (err) {
      setInviteError(err.response?.data?.error || 'Failed to send invitation');
    } finally {
      setInviteLoading(false);
    }
  };

  const handleUpdateRole = async (userId, newRole) => {
    try {
      await axios.put(`${process.env.REACT_APP_API_URL}/api/team/${userId}`, { role: newRole });
      setTeamSuccess('Role updated successfully!');
      setEditingMember(null);
      loadTeamMembers();
      setTimeout(() => setTeamSuccess(''), 3000);
    } catch (err) {
      setTeamError(err.response?.data?.error || 'Failed to update role');
      setTimeout(() => setTeamError(''), 3000);
    }
  };

  const handleRemoveMember = async (userId) => {
    try {
      await axios.delete(`${process.env.REACT_APP_API_URL}/api/team/${userId}`);
      setTeamSuccess('Team member removed');
      setConfirmRemove(null);
      loadTeamMembers();
      setTimeout(() => setTeamSuccess(''), 3000);
    } catch (err) {
      setTeamError(err.response?.data?.error || 'Failed to remove team member');
      setTimeout(() => setTeamError(''), 3000);
    }
  };

  const handleCancelInvitation = async (invitationId) => {
    try {
      await axios.delete(`${process.env.REACT_APP_API_URL}/api/team/invitations/${invitationId}`);
      setTeamSuccess('Invitation cancelled');
      loadTeamInvitations();
      setTimeout(() => setTeamSuccess(''), 3000);
    } catch (err) {
      setTeamError(err.response?.data?.error || 'Failed to cancel invitation');
      setTimeout(() => setTeamError(''), 3000);
    }
  };

  const formatRole = (role) => {
    const roleMap = {
      owner: 'Owner',
      admin: 'Admin',
      crew_lead: 'Crew Lead',
      driver: 'Driver',
    };
    return roleMap[role] || role;
  };

  const getRoleBadgeClass = (role) => {
    const classMap = {
      owner: 'badge-owner',
      admin: 'badge-admin',
      crew_lead: 'badge-crew-lead',
      driver: 'badge-driver',
    };
    return classMap[role] || '';
  };

  const loadPricingTiers = useCallback(async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/pricing-tiers/all?product=lawn`);
      setPricingTiers(response.data.map(t => ({
        ...t,
        default_price: parseFloat(t.default_price).toFixed(2),
        salt_price: parseFloat(t.salt_price || 0).toFixed(2)
      })));
      setTiersModified(false);
    } catch (err) {
      console.error('Failed to load pricing tiers:', err);
    }
  }, []);

  const loadPricingSettings = useCallback(async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/settings/pricing`);
      setPricing(response.data);
    } catch (err) {
      console.error('Failed to load pricing settings:', err);
    }
  }, []);

  const loadPaymentMethods = useCallback(async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/settings/payment-methods`);
      if (response.data && response.data.length > 0) {
        setPaymentMethods(response.data);
      }
    } catch (err) {
      console.error('Failed to load payment methods:', err);
    }
  }, []);

  const handlePaymentMethodToggle = (methodId) => {
    setPaymentMethods(prev =>
      prev.map(m => m.id === methodId ? { ...m, enabled: !m.enabled } : m)
    );
    setPaymentMethodsSuccess('');
    setPaymentMethodsError('');
  };

  const handleAddPaymentMethod = () => {
    if (!newMethodName.trim()) return;
    const id = newMethodName.toLowerCase().replace(/\s+/g, '_');
    if (paymentMethods.some(m => m.id === id)) {
      setPaymentMethodsError('A payment method with that name already exists');
      return;
    }
    setPaymentMethods(prev => [...prev, { id, name: newMethodName.trim(), enabled: true, isBuiltIn: false }]);
    setNewMethodName('');
    setPaymentMethodsSuccess('');
    setPaymentMethodsError('');
  };

  const handleDeletePaymentMethod = (methodId) => {
    setPaymentMethods(prev => prev.filter(m => m.id !== methodId));
    setPaymentMethodsSuccess('');
    setPaymentMethodsError('');
  };

  const handlePaymentMethodsSubmit = async (e) => {
    e.preventDefault();
    setPaymentMethodsLoading(true);
    setPaymentMethodsError('');
    setPaymentMethodsSuccess('');

    try {
      await axios.put(`${process.env.REACT_APP_API_URL}/api/settings/payment-methods`, { methods: paymentMethods });
      setPaymentMethodsSuccess('Payment methods updated successfully');
    } catch (err) {
      setPaymentMethodsError(err.response?.data?.error || 'Failed to update payment methods');
    } finally {
      setPaymentMethodsLoading(false);
    }
  };

  // Lead sources functions
  const loadLeadSources = useCallback(async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/settings/lead-sources`);
      if (response.data && response.data.length > 0) {
        setLeadSources(response.data);
      }
    } catch (err) {
      console.error('Failed to load lead sources:', err);
    }
  }, []);

  const loadExpenseCategories = useCallback(async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/expense-categories?includeInactive=true`);
      if (response.data?.categories) {
        setExpenseCategories(response.data.categories.map(cat => ({
          id: cat.id,
          name: cat.name,
          enabled: cat.is_active,
          isBuiltIn: cat.is_default,
          sortOrder: cat.sort_order,
        })));
      }
    } catch (err) {
      console.error('Failed to load expense categories:', err);
    }
  }, []);

  const loadServiceAddons = useCallback(async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/service-addons/all?product=${PRODUCT_TYPE}`);
      if (response.data) {
        setServiceAddons(response.data.map(addon => ({
          id: addon.id,
          name: addon.name,
          price: parseFloat(addon.price) || 0,
          enabled: addon.is_active,
          sortOrder: addon.sort_order,
        })));
        const tierMap = {};
        for (const addon of response.data) {
          if (addon.tier_prices && addon.tier_prices.length > 0) {
            tierMap[addon.id] = {};
            for (const tp of addon.tier_prices) {
              tierMap[addon.id][tp.pricing_tier_id] = tp.price;
            }
          }
        }
        setAddonTierPrices(tierMap);
      }
    } catch (err) {
      console.error('Failed to load service add-ons:', err);
    }
  }, []);

  const loadCompanyCode = useCallback(async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/settings/company-code`);
      if (response.data?.companyCode) {
        setCompanyCode(response.data.companyCode);
      }
    } catch (err) {
      console.error('Failed to load company code:', err);
    }
  }, []);

  const loadCompanyInfo = useCallback(async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/company?product=${PRODUCT_TYPE}`);
      if (response.data?.custom_slug) {
        setCustomSlug(response.data.custom_slug);
        setCustomSlugInput(response.data.custom_slug);
      }
      // Load invoice defaults
      if (response.data) {
        setInvoiceDefaults({
          defaultInvoiceTitle: response.data.default_invoice_title || '{route_name}',
          defaultInvoiceMessage: response.data.default_invoice_message || 'Thank you for your business!',
          autoInvoiceSquare: response.data.auto_invoice_square || false,
        });
        // Load weather settings
        setWeatherSettings({
          weatherUseHome: response.data.weather_use_home || false,
          weatherCity: response.data.weather_city || '',
          weatherState: response.data.weather_state || '',
        });
        // Load disclaimer settings
        setDisclaimerSettings({
          enabled: response.data.intake_disclaimer_enabled ?? true,
          text: response.data.intake_disclaimer_text || DEFAULT_DISCLAIMER,
        });
        // Load route optimization settings
        setRouteSettings({
          useGps: response.data.route_optimize_use_gps ?? true,
        });
        // Load showcase settings
        setCompany(prev => ({
          ...prev,
          companyDescription: response.data.company_description || '',
          companyTagline: response.data.company_tagline || '',
          showPublicPricing: response.data.show_public_pricing ?? true,
          businessPhone: response.data.business_phone || '',
          businessEmail: response.data.business_email || '',
          standardServices: response.data.standard_services || 'mow,blow',
          seasonalDiscountPercent: parseFloat(response.data.seasonal_discount_percent) || 0,
          seasonalDiscountAmount: parseFloat(response.data.seasonal_discount_amount) || 0,
        }));
      }
    } catch (err) {
      console.error('Failed to load company info:', err);
    }
  }, [DEFAULT_DISCLAIMER]);

  const loadLeadSourceStats = useCallback(async (period = 'all') => {
    setLeadSourceStatsLoading(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/settings/lead-source-stats?period=${period}&product=${PRODUCT_TYPE}`
      );
      setLeadSourceStats(response.data);
    } catch (err) {
      console.error('Failed to load lead source stats:', err);
    } finally {
      setLeadSourceStatsLoading(false);
    }
  }, []);

  const handleLeadSourceToggle = (sourceId) => {
    setLeadSources(prev =>
      prev.map(s => s.id === sourceId ? { ...s, enabled: !s.enabled } : s)
    );
    setLeadSourcesSuccess('');
    setLeadSourcesError('');
  };

  const handleAddLeadSource = () => {
    if (!newSourceName.trim()) return;
    const id = newSourceName.toLowerCase().replace(/\s+/g, '_');
    if (leadSources.some(s => s.id === id)) {
      setLeadSourcesError('A lead source with that name already exists');
      return;
    }
    setLeadSources(prev => [...prev, { id, name: newSourceName.trim(), enabled: true, isBuiltIn: false }]);
    setNewSourceName('');
    setLeadSourcesSuccess('');
    setLeadSourcesError('');
  };

  const handleDeleteLeadSource = (sourceId) => {
    setLeadSources(prev => prev.filter(s => s.id !== sourceId));
    setLeadSourcesSuccess('');
    setLeadSourcesError('');
  };

  const handleLeadSourcesSubmit = async (e) => {
    e.preventDefault();
    setLeadSourcesLoading(true);
    setLeadSourcesError('');
    setLeadSourcesSuccess('');

    try {
      await axios.put(`${process.env.REACT_APP_API_URL}/api/settings/lead-sources`, { sources: leadSources });
      setLeadSourcesSuccess('Lead sources updated successfully');
    } catch (err) {
      setLeadSourcesError(err.response?.data?.error || 'Failed to update lead sources');
    } finally {
      setLeadSourcesLoading(false);
    }
  };

  // Expense category handlers
  const handleExpenseCategoryToggle = (categoryId) => {
    setExpenseCategories(prev =>
      prev.map(c => c.id === categoryId ? { ...c, enabled: !c.enabled } : c)
    );
    setExpenseCategoriesSuccess('');
    setExpenseCategoriesError('');
  };

  const handleAddExpenseCategory = async () => {
    if (!newCategoryName.trim()) return;

    if (expenseCategories.some(c => c.name.toLowerCase() === newCategoryName.trim().toLowerCase())) {
      setExpenseCategoriesError('A category with that name already exists');
      return;
    }

    try {
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/expense-categories`, {
        name: newCategoryName.trim()
      });

      if (response.data?.category) {
        setExpenseCategories(prev => [...prev, {
          id: response.data.category.id,
          name: response.data.category.name,
          enabled: true,
          isBuiltIn: false,
          sortOrder: response.data.category.sort_order,
        }]);
        setNewCategoryName('');
        setExpenseCategoriesSuccess('Category added');
        setTimeout(() => setExpenseCategoriesSuccess(''), 2000);
      }
    } catch (err) {
      setExpenseCategoriesError(err.response?.data?.error || 'Failed to add category');
    }
  };

  const handleDeleteExpenseCategory = async (categoryId) => {
    const category = expenseCategories.find(c => c.id === categoryId);
    if (category?.isBuiltIn) {
      setExpenseCategoriesError('Cannot delete built-in categories. You can disable them instead.');
      return;
    }

    try {
      await axios.delete(`${process.env.REACT_APP_API_URL}/api/expense-categories/${categoryId}`);
      setExpenseCategories(prev => prev.filter(c => c.id !== categoryId));
      setExpenseCategoriesSuccess('Category deleted');
      setTimeout(() => setExpenseCategoriesSuccess(''), 2000);
    } catch (err) {
      setExpenseCategoriesError(err.response?.data?.error || 'Failed to delete category');
    }
  };

  const handleExpenseCategoriesSubmit = async (e) => {
    e.preventDefault();
    setExpenseCategoriesLoading(true);
    setExpenseCategoriesError('');
    setExpenseCategoriesSuccess('');

    try {
      await axios.put(`${process.env.REACT_APP_API_URL}/api/expense-categories`, {
        categories: expenseCategories.map(c => ({
          id: c.id,
          is_active: c.enabled,
          sort_order: c.sortOrder,
        }))
      });
      setExpenseCategoriesSuccess('Expense categories updated successfully');
    } catch (err) {
      setExpenseCategoriesError(err.response?.data?.error || 'Failed to update expense categories');
    } finally {
      setExpenseCategoriesLoading(false);
    }
  };

  // Service add-on handlers
  const handleServiceAddonToggle = (addonId) => {
    setServiceAddons(prev =>
      prev.map(a => a.id === addonId ? { ...a, enabled: !a.enabled } : a)
    );
    setServiceAddonsSuccess('');
    setServiceAddonsError('');
  };

  const handleAddonNameChange = (addonId, newName) => {
    setServiceAddons(prev =>
      prev.map(a => a.id === addonId ? { ...a, name: newName } : a)
    );
    setServiceAddonsSuccess('');
    setServiceAddonsError('');
  };

  const handleAddonPriceChange = (addonId, newPrice) => {
    setServiceAddons(prev =>
      prev.map(a => a.id === addonId ? { ...a, price: parseFloat(newPrice) || 0 } : a)
    );
    setServiceAddonsSuccess('');
    setServiceAddonsError('');
  };

  const handleAddonTierPriceChange = (addonId, tierId, newPrice) => {
    setAddonTierPrices(prev => ({
      ...prev,
      [addonId]: {
        ...(prev[addonId] || {}),
        [tierId]: newPrice === '' ? '' : parseFloat(newPrice) || 0,
      }
    }));
    setServiceAddonsSuccess('');
    setServiceAddonsError('');
  };

  const handleAddServiceAddon = async () => {
    if (!newAddonName.trim()) { setServiceAddonsError('Please enter an add-on name'); return; }
    if (serviceAddons.some(a => a.name.toLowerCase() === newAddonName.trim().toLowerCase())) {
      setServiceAddonsError('An add-on with that name already exists'); return;
    }
    try {
      const newTierPrices = [];
      if (addonTierPrices['new']) {
        for (const [tierId, price] of Object.entries(addonTierPrices['new'])) {
          if (price !== '' && price !== null && price !== undefined) {
            newTierPrices.push({ pricing_tier_id: parseInt(tierId), price: parseFloat(price) || 0 });
          }
        }
      }
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/service-addons`, {
        name: newAddonName.trim(), price: parseFloat(newAddonPrice) || 0, productType: PRODUCT_TYPE, tierPrices: newTierPrices,
      });
      if (response.data) {
        setServiceAddons(prev => [...prev, {
          id: response.data.id, name: response.data.name, price: parseFloat(response.data.price) || 0, enabled: true, sortOrder: response.data.sort_order,
        }]);
        if (addonTierPrices['new']) {
          setAddonTierPrices(prev => { const updated = { ...prev }; updated[response.data.id] = prev['new']; delete updated['new']; return updated; });
        }
        setNewAddonName(''); setNewAddonPrice('');
        setServiceAddonsSuccess('Add-on created'); setTimeout(() => setServiceAddonsSuccess(''), 2000);
      }
    } catch (err) { setServiceAddonsError(err.response?.data?.error || 'Failed to add service add-on'); }
  };

  const handleDeleteServiceAddon = async (addonId) => {
    if (!window.confirm('Delete this add-on? If it has been used on any stops, it will be deactivated instead.')) {
      return;
    }

    try {
      const response = await axios.delete(`${process.env.REACT_APP_API_URL}/api/service-addons/${addonId}`);
      if (response.data?.softDeleted) {
        // Soft deleted - update to show as inactive
        setServiceAddons(prev =>
          prev.map(a => a.id === addonId ? { ...a, enabled: false } : a)
        );
        setServiceAddonsSuccess('Add-on deactivated (it was used on previous stops)');
      } else {
        // Hard deleted - remove from list
        setServiceAddons(prev => prev.filter(a => a.id !== addonId));
        setServiceAddonsSuccess('Add-on deleted');
      }
      setTimeout(() => setServiceAddonsSuccess(''), 2000);
    } catch (err) {
      setServiceAddonsError(err.response?.data?.error || 'Failed to delete add-on');
    }
  };

  const handleServiceAddonsSubmit = async (e) => {
    e.preventDefault();
    setServiceAddonsLoading(true);
    setServiceAddonsError('');
    setServiceAddonsSuccess('');
    try {
      for (const addon of serviceAddons) {
        const tierPricesForAddon = [];
        if (addonTierPrices[addon.id]) {
          for (const [tierId, price] of Object.entries(addonTierPrices[addon.id])) {
            if (price !== '' && price !== null && price !== undefined) {
              tierPricesForAddon.push({ pricing_tier_id: parseInt(tierId), price: parseFloat(price) || 0 });
            }
          }
        }
        await axios.put(`${process.env.REACT_APP_API_URL}/api/service-addons/${addon.id}`, {
          name: addon.name, price: addon.price, isActive: addon.enabled, sortOrder: addon.sortOrder,
          tierPrices: tierPricesForAddon,
        });
      }
      setServiceAddonsSuccess('Service add-ons updated successfully');
      setTimeout(() => setServiceAddonsSuccess(''), 3000);
    } catch (err) {
      setServiceAddonsError(err.response?.data?.error || 'Failed to update service add-ons');
    } finally {
      setServiceAddonsLoading(false);
    }
  };

  // Refresh function for pull-to-refresh
  const handleRefresh = useCallback(async () => {
    await refreshUser();
    await loadMyFeatureRequests();
    if (['owner', 'admin'].includes(user?.role)) {
      await loadPricingSettings();
      await loadPricingTiers();
      await loadPaymentMethods();
      await loadLeadSources();
      await loadExpenseCategories();
      await loadServiceAddons();
      await loadCompanyCode();
      await loadCompanyInfo();
      await loadLeadSourceStats(leadSourceStatsPeriod);
    }
  }, [refreshUser, user?.role, loadPricingSettings, loadPricingTiers, loadPaymentMethods, loadLeadSources, loadExpenseCategories, loadServiceAddons, loadCompanyCode, loadCompanyInfo, loadLeadSourceStats, leadSourceStatsPeriod, loadMyFeatureRequests]);

  const { PullToRefreshIndicator } = usePullToRefresh(handleRefresh);

  useEffect(() => {
    if (user) {
      setProfile({
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        email: user.email || '',
        phone: formatPhoneDisplay(user.phone) || '',
      });
      setCompany(prev => ({
        ...prev,
        name: user.companyName || '',
      }));
      // Load feature requests for all users
      loadMyFeatureRequests();
      // Load team members for all users
      loadTeamMembers();
      // Load pricing settings, payment methods, and lead sources for admins
      if (['owner', 'admin'].includes(user.role)) {
        loadPricingSettings();
        loadPricingTiers();
        loadPaymentMethods();
        loadLeadSources();
        loadExpenseCategories();
        loadServiceAddons();
        loadCompanyCode();
        loadCompanyInfo();
        loadLeadSourceStats();
        loadTeamInvitations();
      }
    }
  }, [user, loadPricingSettings, loadPricingTiers, loadPaymentMethods, loadLeadSources, loadExpenseCategories, loadServiceAddons, loadCompanyCode, loadCompanyInfo, loadLeadSourceStats, loadMyFeatureRequests, loadTeamMembers, loadTeamInvitations]);

  const handleProfileChange = (e) => {
    const { name, value } = e.target;
    // Format phone number as user types
    if (name === 'phone') {
      setProfile({ ...profile, phone: formatPhoneInput(value) });
    } else {
      setProfile({ ...profile, [name]: value });
    }
    setProfileSuccess('');
    setProfileError('');
  };

  const handlePasswordChange = (e) => {
    setPasswords({ ...passwords, [e.target.name]: e.target.value });
    setPasswordSuccess('');
    setPasswordError('');
  };

  const handleCompanyChange = (e) => {
    setCompany({ ...company, [e.target.name]: e.target.value });
    setCompanySuccess('');
    setCompanyError('');
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    setProfileLoading(true);
    setProfileError('');
    setProfileSuccess('');

    try {
      // Strip phone number formatting before sending to API
      const profileData = {
        ...profile,
        phone: stripPhone(profile.phone),
      };
      await axios.put(`${process.env.REACT_APP_API_URL}/api/auth/profile`, profileData);
      setProfileSuccess('Profile updated successfully');
      refreshUser();
    } catch (err) {
      setProfileError(err.response?.data?.error || 'Failed to update profile');
    } finally {
      setProfileLoading(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setPasswordLoading(true);
    setPasswordError('');
    setPasswordSuccess('');

    if (passwords.newPassword !== passwords.confirmPassword) {
      setPasswordError('New passwords do not match');
      setPasswordLoading(false);
      return;
    }

    if (passwords.newPassword.length < 8) {
      setPasswordError('New password must be at least 8 characters');
      setPasswordLoading(false);
      return;
    }

    try {
      await axios.put(`${process.env.REACT_APP_API_URL}/api/auth/password`, {
        currentPassword: passwords.currentPassword,
        newPassword: passwords.newPassword,
      });
      setPasswordSuccess('Password changed successfully');
      setPasswords({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (err) {
      setPasswordError(err.response?.data?.error || 'Failed to change password');
    } finally {
      setPasswordLoading(false);
    }
  };

  const handleCompanySubmit = async (e) => {
    e.preventDefault();
    setCompanyLoading(true);
    setCompanyError('');
    setCompanySuccess('');

    try {
      await axios.put(`${process.env.REACT_APP_API_URL}/api/company?product=${PRODUCT_TYPE}`, {
        name: company.name,
        companyDescription: company.companyDescription,
        companyTagline: company.companyTagline,
        showPublicPricing: company.showPublicPricing,
        businessPhone: company.businessPhone || null,
        businessEmail: company.businessEmail || null,
        standardServices: company.standardServices,
        seasonalDiscountPercent: company.seasonalDiscountPercent || null,
        seasonalDiscountAmount: company.seasonalDiscountAmount || null,
      });
      setCompanySuccess('Company info updated successfully');
      refreshUser();
    } catch (err) {
      setCompanyError(err.response?.data?.error || 'Failed to update company info');
    } finally {
      setCompanyLoading(false);
    }
  };

  const handleInvoiceDefaultsChange = (e) => {
    const { name, value } = e.target;
    setInvoiceDefaults({ ...invoiceDefaults, [name]: value });
    setInvoiceDefaultsSuccess('');
    setInvoiceDefaultsError('');
  };

  const handleInvoiceDefaultsSubmit = async (e) => {
    e.preventDefault();
    setInvoiceDefaultsLoading(true);
    setInvoiceDefaultsError('');
    setInvoiceDefaultsSuccess('');

    try {
      await axios.put(`${process.env.REACT_APP_API_URL}/api/company?product=${PRODUCT_TYPE}`, {
        defaultInvoiceTitle: invoiceDefaults.defaultInvoiceTitle,
        defaultInvoiceMessage: invoiceDefaults.defaultInvoiceMessage,
        autoInvoiceSquare: invoiceDefaults.autoInvoiceSquare,
      });
      setInvoiceDefaultsSuccess('Invoice defaults updated successfully');
    } catch (err) {
      setInvoiceDefaultsError(err.response?.data?.error || 'Failed to update invoice defaults');
    } finally {
      setInvoiceDefaultsLoading(false);
    }
  };

  // Pricing tiers handlers
  const handlePricingTierChange = (tierId, field, value) => {
    setPricingTiers(prev =>
      prev.map(t => t.id === tierId ? { ...t, [field]: value } : t)
    );
    setTiersModified(true);
    setPricingTiersSuccess('');
    setPricingTiersError('');
  };

  const handleAddTier = () => {
    const newTier = {
      id: `new_${Date.now()}`,
      name: '',
      default_price: '0.00',
      salt_price: '0.00',
      is_active: true,
      isNew: true
    };
    setPricingTiers(prev => [...prev, newTier]);
    setTiersModified(true);
    setPricingTiersSuccess('');
    setPricingTiersError('');
  };

  const handleDeleteTier = async (tier) => {
    // For new unsaved tiers, just remove from state
    if (tier.isNew) {
      setPricingTiers(prev => prev.filter(t => t.id !== tier.id));
      return;
    }

    const confirmMsg = `Are you sure you want to delete the "${tier.name}" tier?`;
    if (!window.confirm(confirmMsg)) return;

    try {
      const response = await axios.delete(`${process.env.REACT_APP_API_URL}/api/pricing-tiers/${tier.id}`);
      if (response.data.deactivated) {
        // Tier was deactivated (in use)
        setPricingTiers(prev =>
          prev.map(t => t.id === tier.id ? { ...t, is_active: false } : t)
        );
        setPricingTiersSuccess(`"${tier.name}" tier deactivated (in use by customers)`);
      } else {
        // Tier was deleted
        setPricingTiers(prev => prev.filter(t => t.id !== tier.id));
        setPricingTiersSuccess(`"${tier.name}" tier deleted`);
      }
    } catch (err) {
      setPricingTiersError(err.response?.data?.error || 'Failed to delete tier');
    }
  };

  const handleReactivateTier = async (tier) => {
    try {
      await axios.put(`${process.env.REACT_APP_API_URL}/api/pricing-tiers/${tier.id}`, {
        ...tier,
        is_active: true
      });
      setPricingTiers(prev =>
        prev.map(t => t.id === tier.id ? { ...t, is_active: true } : t)
      );
      setPricingTiersSuccess(`"${tier.name}" tier reactivated`);
    } catch (err) {
      setPricingTiersError(err.response?.data?.error || 'Failed to reactivate tier');
    }
  };

  const handlePricingTiersSubmit = async (e) => {
    e.preventDefault();
    setPricingTiersLoading(true);
    setPricingTiersError('');
    setPricingTiersSuccess('');

    // Validate tiers
    const activeTiers = pricingTiers.filter(t => t.is_active);
    for (const tier of activeTiers) {
      if (!tier.name.trim()) {
        setPricingTiersError('All tiers must have a name');
        setPricingTiersLoading(false);
        return;
      }
      if (parseFloat(tier.default_price) < 0 || parseFloat(tier.salt_price) < 0) {
        setPricingTiersError('Prices cannot be negative');
        setPricingTiersLoading(false);
        return;
      }
    }

    try {
      // Separate new tiers from existing
      const newTiers = pricingTiers.filter(t => t.isNew);
      const existingTiers = pricingTiers.filter(t => !t.isNew);

      // Create new tiers
      for (const tier of newTiers) {
        await axios.post(`${process.env.REACT_APP_API_URL}/api/pricing-tiers`, {
          name: tier.name,
          default_price: parseFloat(tier.default_price),
          salt_price: parseFloat(tier.salt_price),
          product_type: 'lawn'
        });
      }

      // Bulk update existing tiers
      if (existingTiers.length > 0) {
        await axios.put(`${process.env.REACT_APP_API_URL}/api/pricing-tiers`, {
          tiers: existingTiers.map(t => ({
            id: t.id,
            name: t.name,
            default_price: parseFloat(t.default_price),
            salt_price: parseFloat(t.salt_price),
            is_active: t.is_active
          }))
        });
      }

      // Reload to get fresh data with proper IDs
      await loadPricingTiers();
      setPricingTiersSuccess('Pricing tiers saved successfully');
    } catch (err) {
      setPricingTiersError(err.response?.data?.error || 'Failed to save pricing tiers');
    } finally {
      setPricingTiersLoading(false);
    }
  };

  const handlePricingChange = (e) => {
    const { name, value } = e.target;
    setPricing({ ...pricing, [name]: value });
    setPricingSuccess('');
    setPricingError('');
  };

  const handlePricingSubmit = async (e) => {
    e.preventDefault();
    setPricingLoading(true);
    setPricingError('');
    setPricingSuccess('');

    try {
      await axios.put(`${process.env.REACT_APP_API_URL}/api/settings/pricing`, pricing);
      setPricingSuccess('Pricing settings updated successfully');
    } catch (err) {
      setPricingError(err.response?.data?.error || 'Failed to update pricing settings');
    } finally {
      setPricingLoading(false);
    }
  };

  // Feature request handlers
  const handleFeatureRequestChange = (e) => {
    const { name, value } = e.target;
    setFeatureRequest({ ...featureRequest, [name]: value });
    setFeatureRequestSuccess('');
    setFeatureRequestError('');
  };

  const handleFeatureRequestSubmit = async (e) => {
    e.preventDefault();
    if (!featureRequest.subject.trim() || !featureRequest.description.trim()) {
      setFeatureRequestError('Please fill in both subject and description');
      return;
    }

    setFeatureRequestLoading(true);
    setFeatureRequestError('');
    setFeatureRequestSuccess('');

    try {
      await axios.post(`${process.env.REACT_APP_API_URL}/api/feature-requests`, featureRequest);
      setFeatureRequestSuccess('Request submitted! Thanks for your feedback.');
      setFeatureRequest({ subject: '', description: '' });
      loadMyFeatureRequests(); // Reload the list
    } catch (err) {
      setFeatureRequestError(err.response?.data?.error || 'Failed to submit request');
    } finally {
      setFeatureRequestLoading(false);
    }
  };

  // Weather settings handlers
  const handleWeatherSettingsChange = (e) => {
    const { name, value, type } = e.target;
    setWeatherSettings(prev => ({
      ...prev,
      [name]: type === 'radio' ? value === 'true' : value
    }));
    setWeatherSettingsSuccess('');
    setWeatherSettingsError('');
  };

  const handleWeatherSettingsSubmit = async (e) => {
    e.preventDefault();

    // Validate if using home location
    if (weatherSettings.weatherUseHome && (!weatherSettings.weatherCity.trim() || !weatherSettings.weatherState)) {
      setWeatherSettingsError('Please enter a city and state for home location');
      return;
    }

    setWeatherSettingsLoading(true);
    setWeatherSettingsError('');
    setWeatherSettingsSuccess('');

    try {
      await axios.put(`${process.env.REACT_APP_API_URL}/api/company?product=${PRODUCT_TYPE}`, {
        weatherCity: weatherSettings.weatherCity.trim() || null,
        weatherState: weatherSettings.weatherState || null,
        weatherUseHome: weatherSettings.weatherUseHome,
      });
      setWeatherSettingsSuccess('Weather settings saved!');
    } catch (err) {
      setWeatherSettingsError(err.response?.data?.error || 'Failed to save weather settings');
    } finally {
      setWeatherSettingsLoading(false);
    }
  };

  // Disclaimer settings handlers
  const handleDisclaimerSettingsChange = (e) => {
    const { name, value, type, checked } = e.target;
    setDisclaimerSettings(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    setDisclaimerSettingsSuccess('');
    setDisclaimerSettingsError('');
  };

  const handleDisclaimerSettingsSubmit = async (e) => {
    e.preventDefault();
    setDisclaimerSettingsLoading(true);
    setDisclaimerSettingsError('');
    setDisclaimerSettingsSuccess('');

    try {
      await axios.put(`${process.env.REACT_APP_API_URL}/api/company?product=${PRODUCT_TYPE}`, {
        intakeDisclaimerEnabled: disclaimerSettings.enabled,
        intakeDisclaimerText: disclaimerSettings.text,
      });
      setDisclaimerSettingsSuccess('Disclaimer settings saved!');
    } catch (err) {
      setDisclaimerSettingsError(err.response?.data?.error || 'Failed to save disclaimer settings');
    } finally {
      setDisclaimerSettingsLoading(false);
    }
  };

  const handleResetDisclaimer = () => {
    setDisclaimerSettings(prev => ({
      ...prev,
      text: DEFAULT_DISCLAIMER
    }));
    setDisclaimerSettingsSuccess('');
    setDisclaimerSettingsError('');
  };

  // Route optimization settings handlers
  const handleRouteSettingsChange = (e) => {
    const { name, checked } = e.target;
    setRouteSettings(prev => ({
      ...prev,
      [name]: checked
    }));
    setRouteSettingsSuccess('');
    setRouteSettingsError('');
  };

  const handleRouteSettingsSubmit = async (e) => {
    e.preventDefault();
    setRouteSettingsLoading(true);
    setRouteSettingsError('');
    setRouteSettingsSuccess('');

    try {
      await axios.put(`${process.env.REACT_APP_API_URL}/api/company?product=${PRODUCT_TYPE}`, {
        routeOptimizeUseGps: routeSettings.useGps,
      });
      setRouteSettingsSuccess('Route settings saved!');
    } catch (err) {
      setRouteSettingsError(err.response?.data?.error || 'Failed to save route settings');
    } finally {
      setRouteSettingsLoading(false);
    }
  };

  // Custom slug handlers
  const validateSlug = (slug) => {
    if (!slug) return true; // Empty is valid (clears the slug)
    const slugRegex = /^[a-z0-9][a-z0-9-]{1,48}[a-z0-9]$/;
    return slugRegex.test(slug) && slug.length >= 3 && slug.length <= 50;
  };

  const handleSlugInputChange = (e) => {
    const value = e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '');
    setCustomSlugInput(value);
    setSlugError('');
    setSlugSuccess('');
    setSlugAvailable(null);

    // Clear any pending timeout
    if (slugCheckTimeout) {
      clearTimeout(slugCheckTimeout);
    }

    // Debounced availability check
    if (value && value.length >= 3) {
      const timeout = setTimeout(async () => {
        if (!validateSlug(value)) {
          setSlugError('Must be 3-50 characters, start/end with letter or number');
          return;
        }
        // Don't check if it's the same as current slug
        if (value === customSlug) {
          setSlugAvailable(null);
          return;
        }
        setSlugChecking(true);
        try {
          const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/company/check-slug/${value}?product=${PRODUCT_TYPE}`);
          setSlugAvailable(response.data.available);
          if (!response.data.available) {
            setSlugError('This URL is already taken');
          }
        } catch (err) {
          console.error('Failed to check slug:', err);
        } finally {
          setSlugChecking(false);
        }
      }, 500);
      setSlugCheckTimeout(timeout);
    }
  };

  const handleSlugSave = async () => {
    setSlugError('');
    setSlugSuccess('');

    // Validate before saving
    if (customSlugInput && !validateSlug(customSlugInput)) {
      setSlugError('Must be 3-50 characters, start/end with letter or number, only lowercase letters, numbers, and hyphens');
      return;
    }

    setSlugSaving(true);
    try {
      const response = await axios.put(`${process.env.REACT_APP_API_URL}/api/company/slug?product=${PRODUCT_TYPE}`, {
        slug: customSlugInput || null
      });
      setCustomSlug(response.data.slug || '');
      setSlugSuccess(customSlugInput ? 'Custom URL saved!' : 'Custom URL cleared');
      setSlugAvailable(null);
    } catch (err) {
      setSlugError(err.response?.data?.error || 'Failed to save custom URL');
    } finally {
      setSlugSaving(false);
    }
  };

  const handleClearSlug = async () => {
    setCustomSlugInput('');
    setSlugError('');
    setSlugSuccess('');
    setSlugAvailable(null);

    if (customSlug) {
      // If there was a saved slug, clear it on the server
      setSlugSaving(true);
      try {
        await axios.put(`${process.env.REACT_APP_API_URL}/api/company/slug?product=${PRODUCT_TYPE}`, { slug: null });
        setCustomSlug('');
        setSlugSuccess('Custom URL cleared');
      } catch (err) {
        setSlugError(err.response?.data?.error || 'Failed to clear custom URL');
      } finally {
        setSlugSaving(false);
      }
    }
  };

  return (
    <div className="settings-page">
      <PullToRefreshIndicator />
      {/* Header */}
      <header className="app-header">
        <div className="app-header-content">
          <span className="app-logo" onClick={() => navigate('/dashboard')} style={{ cursor: 'pointer' }}>
            {getAppName()}
          </span>
          <nav className="header-nav desktop-nav">
            <button onClick={() => navigate('/dashboard')} className="nav-link">Dashboard</button>
            {!isDriver() && <button onClick={() => navigate('/customers')} className="nav-link">Customers</button>}
            <button onClick={() => navigate('/routes')} className="nav-link">Routes</button>
            {isAdmin() && <button onClick={() => navigate('/history')} className="nav-link">History</button>}
            {isAdmin() && <button onClick={() => navigate('/expenses')} className="nav-link">Expenses</button>}
            {isAdmin() && <button onClick={() => navigate('/reports')} className="nav-link">Reports</button>}
            <button onClick={() => navigate('/service-mode')} className="nav-link storm-link">Service Mode</button>
            <span className="nav-link active">Settings</span>
            <button onClick={() => navigate('/docs')} className="nav-link help-link" title="Help">?</button>
            {isSuperAdmin() && (
              <button onClick={() => navigate('/admin')} className="nav-link admin-link">Admin</button>
            )}
            <button onClick={logout} className="nav-link logout-link">Logout</button>
          </nav>
          <button className="mobile-menu-btn" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
            <span></span>
            <span></span>
            <span></span>
          </button>
        </div>
        {mobileMenuOpen && (
          <nav className="mobile-nav">
            <button onClick={() => { navigate('/dashboard'); setMobileMenuOpen(false); }} className="mobile-nav-link">Dashboard</button>
            {!isDriver() && <button onClick={() => { navigate('/customers'); setMobileMenuOpen(false); }} className="mobile-nav-link">Customers</button>}
            <button onClick={() => { navigate('/routes'); setMobileMenuOpen(false); }} className="mobile-nav-link">Routes</button>
            {isAdmin() && <button onClick={() => { navigate('/history'); setMobileMenuOpen(false); }} className="mobile-nav-link">History</button>}
            {isAdmin() && <button onClick={() => { navigate('/expenses'); setMobileMenuOpen(false); }} className="mobile-nav-link">Expenses</button>}
            {isAdmin() && <button onClick={() => { navigate('/reports'); setMobileMenuOpen(false); }} className="mobile-nav-link">Reports</button>}
            <button onClick={() => { navigate('/service-mode'); setMobileMenuOpen(false); }} className="mobile-nav-link storm-link">Service Mode</button>
            <span className="mobile-nav-link active">Settings</span>
            <button onClick={() => { navigate('/docs'); setMobileMenuOpen(false); }} className="mobile-nav-link">Help</button>
            {isSuperAdmin() && (
              <button onClick={() => { navigate('/admin'); setMobileMenuOpen(false); }} className="mobile-nav-link admin-link">Admin</button>
            )}
            <button onClick={() => { logout(); setMobileMenuOpen(false); }} className="mobile-nav-link logout-link">Logout</button>
          </nav>
        )}
      </header>

      <div className="page-container">
        <div className="page-header">
          <h1 className="page-title">Settings</h1>
          <p className="text-muted">Manage your profile and account settings</p>
        </div>

        {/* Account Banner */}
        <div className="account-banner">
          <div className="account-banner-name">
            <span className="account-icon">👤</span>
            {user?.firstName} {user?.lastName}
          </div>
          <div className="account-banner-details">
            <span className="account-detail">
              <span className="detail-label">Role:</span>
              <span className="detail-value">
                {user?.role === 'owner' && 'Owner'}
                {user?.role === 'admin' && 'Administrator'}
                {user?.role === 'crew_lead' && 'Crew Lead'}
                {user?.role === 'driver' && 'Driver'}
              </span>
            </span>
            <span className="account-divider">|</span>
            <span className="account-detail">
              <span className="detail-label">Company:</span>
              <span className="detail-value">{user?.companyName}</span>
            </span>
          </div>
        </div>

        {/* Profile & Account Section */}
        <div className={`settings-section ${expandedSections.profile ? 'expanded' : 'collapsed'}`}>
          <div className="settings-section-header" onClick={() => toggleSection('profile')}>
            <span className="section-arrow">{expandedSections.profile ? '\u25BC' : '\u25B6'}</span>
            <h2 className="section-title">Profile & Account</h2>
          </div>
          <div className="settings-section-content">
            <div className="card mb-lg">
              <div className="card-header">
                <h2 className="card-title">Profile Information</h2>
              </div>

          <form onSubmit={handleProfileSubmit}>
            {profileError && <div className="alert alert-error">{profileError}</div>}
            {profileSuccess && <div className="alert alert-success">{profileSuccess}</div>}

            <div className="grid grid-2">
              <div className="form-group">
                <label className="form-label" htmlFor="firstName">First Name</label>
                <input
                  type="text"
                  id="firstName"
                  name="firstName"
                  className="form-input"
                  value={profile.firstName}
                  onChange={handleProfileChange}
                  required
                />
              </div>

              <div className="form-group">
                <label className="form-label" htmlFor="lastName">Last Name</label>
                <input
                  type="text"
                  id="lastName"
                  name="lastName"
                  className="form-input"
                  value={profile.lastName}
                  onChange={handleProfileChange}
                  required
                />
              </div>
            </div>

            <div className="form-group">
              <label className="form-label" htmlFor="email">Email</label>
              <input
                type="email"
                id="email"
                name="email"
                className="form-input"
                value={profile.email}
                onChange={handleProfileChange}
                required
              />
            </div>

            <div className="form-group">
              <label className="form-label" htmlFor="phone">Phone</label>
              <input
                type="tel"
                id="phone"
                name="phone"
                className="form-input"
                value={profile.phone}
                onChange={handleProfileChange}
                placeholder="(555) 123-4567"
              />
            </div>

            <button type="submit" className="btn btn-primary" disabled={profileLoading}>
              {profileLoading ? 'Saving...' : 'Save Profile'}
            </button>
          </form>
        </div>

        {/* Password Section */}
        <div className="card mb-lg">
          <div className="card-header">
            <h2 className="card-title">Change Password</h2>
          </div>

          <form onSubmit={handlePasswordSubmit}>
            {passwordError && <div className="alert alert-error">{passwordError}</div>}
            {passwordSuccess && <div className="alert alert-success">{passwordSuccess}</div>}

            <div className="form-group">
              <label className="form-label" htmlFor="currentPassword">Current Password</label>
              <input
                type="password"
                id="currentPassword"
                name="currentPassword"
                className="form-input"
                value={passwords.currentPassword}
                onChange={handlePasswordChange}
                required
              />
            </div>

            <div className="form-group">
              <label className="form-label" htmlFor="newPassword">New Password</label>
              <input
                type="password"
                id="newPassword"
                name="newPassword"
                className="form-input"
                value={passwords.newPassword}
                onChange={handlePasswordChange}
                placeholder="At least 8 characters"
                required
              />
            </div>

            <div className="form-group">
              <label className="form-label" htmlFor="confirmPassword">Confirm New Password</label>
              <input
                type="password"
                id="confirmPassword"
                name="confirmPassword"
                className="form-input"
                value={passwords.confirmPassword}
                onChange={handlePasswordChange}
                required
              />
            </div>

            <button type="submit" className="btn btn-primary" disabled={passwordLoading}>
              {passwordLoading ? 'Changing...' : 'Change Password'}
            </button>
          </form>
        </div>

          </div>
        </div>

        {/* Team Management Section */}
        {isAdmin() && (
        <div className={`settings-section ${expandedSections.team ? 'expanded' : 'collapsed'}`}>
          <div className="settings-section-header" onClick={() => toggleSection('team')}>
            <span className="section-arrow">{expandedSections.team ? '\u25BC' : '\u25B6'}</span>
            <h2 className="section-title">Team Management</h2>
          </div>
          <div className="settings-section-content">
            <div className="card mb-lg">
              <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <h2 className="card-title">Team Members</h2>
                  <p className="text-muted" style={{ fontSize: '0.875rem', marginTop: '4px' }}>
                    {isAdmin() ? 'Manage your team and send invitations' : 'View your team members'}
                  </p>
                </div>
                {isAdmin() && (
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() => setShowInviteModal(true)}
                  >
                    + Invite Team Member
                  </button>
                )}
              </div>

              {teamError && <div className="alert alert-error">{teamError}</div>}
              {teamSuccess && <div className="alert alert-success">{teamSuccess}</div>}

              {teamLoading ? (
                <div className="loading-container" style={{ padding: '24px', textAlign: 'center' }}>
                  <div className="loading-spinner"></div>
                </div>
              ) : (
                <div className="team-list">
                  {teamMembers.map(member => (
                    <div key={member.id} className="team-member-row">
                      <div className="team-member-info">
                        <div className="team-member-name">
                          {member.first_name} {member.last_name}
                          {member.id === user?.id && <span className="you-badge">(You)</span>}
                        </div>
                        <div className="team-member-email">{member.email}</div>
                      </div>
                      <div className="team-member-actions">
                        {editingMember === member.id ? (
                          <select
                            className="form-input role-select"
                            value={member.role}
                            onChange={(e) => handleUpdateRole(member.id, e.target.value)}
                            onBlur={() => setEditingMember(null)}
                            autoFocus
                          >
                            {isOwner() && <option value="admin">Admin</option>}
                            <option value="crew_lead">Crew Lead</option>
                            <option value="driver">Driver</option>
                          </select>
                        ) : (
                          <span className={`badge ${getRoleBadgeClass(member.role)}`}>
                            {formatRole(member.role)}
                          </span>
                        )}
                        {isAdmin() && member.role !== 'owner' && member.id !== user?.id && (
                          <>
                            <button
                              type="button"
                              className="btn btn-secondary btn-sm"
                              onClick={() => setEditingMember(member.id)}
                              title="Change role"
                            >
                              Edit
                            </button>
                            {confirmRemove === member.id ? (
                              <div className="confirm-remove">
                                <span>Remove?</span>
                                <button
                                  type="button"
                                  className="btn btn-error btn-sm"
                                  onClick={() => handleRemoveMember(member.id)}
                                >
                                  Yes
                                </button>
                                <button
                                  type="button"
                                  className="btn btn-secondary btn-sm"
                                  onClick={() => setConfirmRemove(null)}
                                >
                                  No
                                </button>
                              </div>
                            ) : (
                              <button
                                type="button"
                                className="btn btn-error btn-sm"
                                onClick={() => setConfirmRemove(member.id)}
                                title="Remove from team"
                              >
                                Remove
                              </button>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Pending Invitations */}
              {isAdmin() && teamInvitations.length > 0 && (
                <div className="pending-invitations">
                  <h3 className="subsection-title">Pending Invitations</h3>
                  {teamInvitations.map(invite => (
                    <div key={invite.id} className="invitation-row">
                      <div className="invitation-info">
                        <div className="invitation-email">{invite.email}</div>
                        <div className="invitation-meta">
                          <span className={`badge ${getRoleBadgeClass(invite.role)}`}>
                            {formatRole(invite.role)}
                          </span>
                          <span className="invitation-expires">
                            Expires {new Date(invite.expires_at).toLocaleDateString()}
                          </span>
                        </div>
                      </div>
                      <button
                        type="button"
                        className="btn btn-secondary btn-sm"
                        onClick={() => handleCancelInvitation(invite.id)}
                      >
                        Cancel
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
        )}

        {/* Invite Modal */}
        {isAdmin() && showInviteModal && (
          <div className="modal-overlay" onClick={() => setShowInviteModal(false)}>
            <div className="modal" onClick={e => e.stopPropagation()}>
              <div className="modal-header">
                <h2>Invite Team Member</h2>
                <button className="modal-close" onClick={() => setShowInviteModal(false)}>&times;</button>
              </div>
              <form onSubmit={handleSendInvite}>
                {inviteError && <div className="alert alert-error">{inviteError}</div>}

                <div className="form-group">
                  <label className="form-label" htmlFor="inviteEmail">Email Address</label>
                  <input
                    type="email"
                    id="inviteEmail"
                    className="form-input"
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                    placeholder="teammate@example.com"
                    required
                    autoFocus
                  />
                </div>

                <div className="form-group">
                  <label className="form-label" htmlFor="inviteRole">Role</label>
                  <select
                    id="inviteRole"
                    className="form-input"
                    value={inviteRole}
                    onChange={(e) => setInviteRole(e.target.value)}
                  >
                    {isOwner() && <option value="admin">Admin - Full access, can manage team</option>}
                    <option value="crew_lead">Crew Lead - Can manage routes</option>
                    <option value="driver">Driver - Can view assigned routes only</option>
                  </select>
                </div>

                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={() => setShowInviteModal(false)}>
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary" disabled={inviteLoading}>
                    {inviteLoading ? 'Sending...' : 'Send Invitation'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Company Section */}
        {isAdmin() && (
          <div className={`settings-section ${expandedSections.company ? 'expanded' : 'collapsed'}`}>
            <div className="settings-section-header" onClick={() => toggleSection('company')}>
              <span className="section-arrow">{expandedSections.company ? '\u25BC' : '\u25B6'}</span>
              <h2 className="section-title">Company</h2>
            </div>
            <div className="settings-section-content">
              <div className="card mb-lg">
                <div className="card-header">
                  <h2 className="card-title">Company Information</h2>
              {!isOwner() && (
                <p className="text-muted" style={{ fontSize: '0.875rem', marginTop: '4px' }}>
                  As an admin, you can view and edit company settings
                </p>
              )}
            </div>

            <form onSubmit={handleCompanySubmit}>
              {companyError && <div className="alert alert-error">{companyError}</div>}
              {companySuccess && <div className="alert alert-success">{companySuccess}</div>}

              <div className="form-group">
                <label className="form-label" htmlFor="companyName">Company Name</label>
                <input
                  type="text"
                  id="companyName"
                  name="name"
                  className="form-input"
                  value={company.name}
                  onChange={handleCompanyChange}
                  required
                />
              </div>

              <div className="form-group">
                <label className="form-label">Subscription</label>
                <div className="subscription-info">
                  <span className="badge badge-info">{user?.subscriptionTier || 'FREE_TRIAL'}</span>
                  {user?.trialEndsAt && (
                    <span className="text-muted" style={{ marginLeft: '8px', fontSize: '0.875rem' }}>
                      Trial ends: {new Date(user.trialEndsAt).toLocaleDateString()}
                    </span>
                  )}
                </div>
              </div>

              <div className="form-group">
                <label className="form-label" htmlFor="companyTagline">Company Tagline</label>
                <input
                  type="text"
                  id="companyTagline"
                  name="companyTagline"
                  className="form-input"
                  value={company.companyTagline}
                  onChange={handleCompanyChange}
                  placeholder="e.g., Professional lawn care you can count on"
                  maxLength={255}
                />
                <small className="text-muted">Short tagline shown on your public business page</small>
              </div>

              <div className="form-group">
                <label className="form-label" htmlFor="companyDescription">Company Description</label>
                <textarea
                  id="companyDescription"
                  name="companyDescription"
                  className="form-input"
                  value={company.companyDescription}
                  onChange={handleCompanyChange}
                  placeholder="Tell potential customers about your business, experience, and what sets you apart..."
                  rows={4}
                  style={{ resize: 'vertical', minHeight: '80px' }}
                />
                <small className="text-muted">Shown in the About section of your public business page</small>
              </div>

              {/* Business Contact Info */}
              <div className="form-group">
                <label className="form-label">Business Contact Info</label>
                <small className="text-muted" style={{ display: 'block', marginBottom: '8px' }}>
                  Leave blank to use your personal contact info on your public page
                </small>
                <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                  <div style={{ flex: 1, minWidth: '200px' }}>
                    <input
                      type="tel"
                      name="businessPhone"
                      className="form-input"
                      value={company.businessPhone}
                      onChange={handleCompanyChange}
                      placeholder="(555) 123-4567"
                    />
                  </div>
                  <div style={{ flex: 1, minWidth: '200px' }}>
                    <input
                      type="email"
                      name="businessEmail"
                      className="form-input"
                      value={company.businessEmail}
                      onChange={handleCompanyChange}
                      placeholder="info@yourbusiness.com"
                    />
                  </div>
                </div>
              </div>

              {/* Standard Services Included */}
              <div className="form-group">
                <label className="form-label">Standard Services Included</label>
                <small className="text-muted" style={{ display: 'block', marginBottom: '8px' }}>
                  Services included with every visit — shown on your public page
                </small>
                <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                  {['mow', 'edge', 'trim', 'blow'].map(service => (
                    <label key={service} style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={(company.standardServices || '').split(',').includes(service)}
                        onChange={(e) => {
                          const current = (company.standardServices || '').split(',').filter(Boolean);
                          const updated = e.target.checked
                            ? [...current, service]
                            : current.filter(s => s !== service);
                          setCompany({ ...company, standardServices: updated.join(',') });
                          setCompanySuccess('');
                          setCompanyError('');
                        }}
                        style={{ accentColor: '#4CAF50', width: '18px', height: '18px' }}
                      />
                      {service.charAt(0).toUpperCase() + service.slice(1)}
                    </label>
                  ))}
                </div>
              </div>

              {/* Seasonal Contract Discount */}
              <div className="form-group">
                <label className="form-label">Seasonal Contract Discount</label>
                <small className="text-muted" style={{ display: 'block', marginBottom: '8px' }}>
                  Customers who sign up for seasonal service get this discount off regular pricing
                </small>
                <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
                  <select
                    className="form-input"
                    style={{ width: 'auto', minWidth: '160px' }}
                    value={company.seasonalDiscountPercent > 0 ? 'percent' : company.seasonalDiscountAmount > 0 ? 'dollar' : 'percent'}
                    onChange={(e) => {
                      if (e.target.value === 'percent') {
                        setCompany({ ...company, seasonalDiscountAmount: 0 });
                      } else {
                        setCompany({ ...company, seasonalDiscountPercent: 0 });
                      }
                      setCompanySuccess('');
                      setCompanyError('');
                    }}
                  >
                    <option value="percent">Percentage (%)</option>
                    <option value="dollar">Dollar Amount ($)</option>
                  </select>
                  <input
                    type="number"
                    className="form-input"
                    style={{ width: '120px' }}
                    min="0"
                    step={company.seasonalDiscountPercent > 0 || company.seasonalDiscountAmount === 0 ? '1' : '0.01'}
                    value={company.seasonalDiscountPercent > 0 ? company.seasonalDiscountPercent : company.seasonalDiscountAmount}
                    onChange={(e) => {
                      const val = parseFloat(e.target.value) || 0;
                      if (company.seasonalDiscountPercent > 0 || (company.seasonalDiscountPercent === 0 && company.seasonalDiscountAmount === 0)) {
                        setCompany({ ...company, seasonalDiscountPercent: val, seasonalDiscountAmount: 0 });
                      } else {
                        setCompany({ ...company, seasonalDiscountAmount: val, seasonalDiscountPercent: 0 });
                      }
                      setCompanySuccess('');
                      setCompanyError('');
                    }}
                    placeholder="0"
                  />
                  <span className="text-muted" style={{ fontSize: '0.875rem' }}>
                    {company.seasonalDiscountPercent > 0
                      ? `${company.seasonalDiscountPercent}% off per visit`
                      : company.seasonalDiscountAmount > 0
                        ? `$${parseFloat(company.seasonalDiscountAmount).toFixed(2)} off per visit`
                        : 'No seasonal discount'}
                  </span>
                </div>
              </div>

              <div className="form-group">
                <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    name="showPublicPricing"
                    checked={company.showPublicPricing}
                    onChange={(e) => {
                      setCompany({ ...company, showPublicPricing: e.target.checked });
                      setCompanySuccess('');
                      setCompanyError('');
                    }}
                    style={{ accentColor: '#4CAF50', width: '18px', height: '18px' }}
                  />
                  Show Pricing on Public Website
                </label>
                <small className="text-muted">When enabled, your pricing tiers and add-on prices are visible on your business page</small>
              </div>

              <button type="submit" className="btn btn-primary" disabled={companyLoading}>
                {companyLoading ? 'Saving...' : 'Save Company Info'}
              </button>
            </form>
              </div>

              {/* Business Website URL Card */}
              <div className="card mb-lg">
                <div className="card-header">
                  <h2 className="card-title">Your Business Website</h2>
                  <p className="text-muted" style={{ fontSize: '0.875rem', marginTop: '4px' }}>
                    Share this link to showcase your business, services, and pricing to potential customers
                  </p>
                </div>

                <div className="intake-link-section">
                  <label className="form-label">Website Link:</label>
                  <div className="intake-link-row">
                    <input
                      type="text"
                      className="form-input intake-link-input"
                      value={`${window.location.origin}/company/${customSlug || companyCode}`}
                      readOnly
                      onClick={(e) => e.target.select()}
                    />
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={() => {
                        navigator.clipboard.writeText(`${window.location.origin}/company/${customSlug || companyCode}`);
                        setCopiedLink(true);
                        setTimeout(() => setCopiedLink(false), 2000);
                      }}
                    >
                      {copiedLink ? 'Copied!' : 'Copy Link'}
                    </button>
                  </div>
                  <small className="text-muted">
                    This page shows your company info, pricing, and a sign-up button for new customers.
                  </small>
                </div>
              </div>

              {/* Customer Portal URL Card */}
              <div className="card mb-lg">
                <div className="card-header">
                  <h2 className="card-title">Customer Portal URL</h2>
                  <p className="text-muted" style={{ fontSize: '0.875rem', marginTop: '4px' }}>
                    Share this link so customers can view their service history and invoices
                  </p>
                </div>

                <div className="intake-link-section">
                  <label className="form-label">Portal Link:</label>
                  <div className="intake-link-row">
                    <input
                      type="text"
                      className="form-input intake-link-input"
                      value={`${window.location.origin}/portal/${customSlug || companyCode}`}
                      readOnly
                      onClick={(e) => e.target.select()}
                    />
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={() => {
                        navigator.clipboard.writeText(`${window.location.origin}/portal/${customSlug || companyCode}`);
                        setCopiedLink(true);
                        setTimeout(() => setCopiedLink(false), 2000);
                      }}
                    >
                      {copiedLink ? 'Copied!' : 'Copy Link'}
                    </button>
                  </div>
                  <small className="text-muted">
                    {customSlug
                      ? 'Using your custom URL. Change it in the Customer Intake Form section below.'
                      : 'Set a custom URL in the Customer Intake Form section below.'}
                  </small>
                </div>
              </div>

              <div className="card mb-lg">
                <div className="card-header">
                  <h2 className="card-title">Weather Location</h2>
                  <p className="text-muted" style={{ fontSize: '0.875rem', marginTop: '4px' }}>
                    Set where weather forecasts should be based from
                  </p>
                </div>

                <form onSubmit={handleWeatherSettingsSubmit}>
                  {weatherSettingsError && <div className="alert alert-error">{weatherSettingsError}</div>}
                  {weatherSettingsSuccess && <div className="alert alert-success">{weatherSettingsSuccess}</div>}

                  <div className="form-group">
                    <label className="radio-label">
                      <input
                        type="radio"
                        name="weatherUseHome"
                        value="false"
                        checked={!weatherSettings.weatherUseHome}
                        onChange={handleWeatherSettingsChange}
                      />
                      <span className="radio-text">Use my current location (GPS)</span>
                    </label>
                  </div>

                  <div className="form-group">
                    <label className="radio-label">
                      <input
                        type="radio"
                        name="weatherUseHome"
                        value="true"
                        checked={weatherSettings.weatherUseHome}
                        onChange={handleWeatherSettingsChange}
                      />
                      <span className="radio-text">Use home location:</span>
                    </label>
                  </div>

                  {weatherSettings.weatherUseHome && (
                    <div className="form-row" style={{ display: 'flex', gap: '12px', marginTop: '12px', marginBottom: '16px' }}>
                      <div className="form-group" style={{ flex: 2, marginBottom: 0 }}>
                        <label className="form-label" htmlFor="weatherCity">City</label>
                        <input
                          type="text"
                          id="weatherCity"
                          name="weatherCity"
                          className="form-input"
                          value={weatherSettings.weatherCity}
                          onChange={handleWeatherSettingsChange}
                          placeholder="e.g., Appleton"
                          maxLength={100}
                        />
                      </div>
                      <div className="form-group" style={{ flex: 1, marginBottom: 0 }}>
                        <label className="form-label" htmlFor="weatherState">State</label>
                        <select
                          id="weatherState"
                          name="weatherState"
                          className="form-select"
                          value={weatherSettings.weatherState}
                          onChange={handleWeatherSettingsChange}
                        >
                          <option value="">Select</option>
                          {US_STATES.map(state => (
                            <option key={state} value={state}>{state}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                  )}

                  <button type="submit" className="btn btn-primary" disabled={weatherSettingsLoading}>
                    {weatherSettingsLoading ? 'Saving...' : 'Save Weather Settings'}
                  </button>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Pricing & Billing Section */}
        {isAdmin() && (
          <div className={`settings-section ${expandedSections.pricing ? 'expanded' : 'collapsed'}`}>
            <div className="settings-section-header" onClick={() => toggleSection('pricing')}>
              <span className="section-arrow">{expandedSections.pricing ? '\u25BC' : '\u25B6'}</span>
              <h2 className="section-title">Pricing & Billing</h2>
            </div>
            <div className="settings-section-content">
              <div className="card mb-lg">
                <div className="card-header">
                  <h2 className="card-title">Referral Discount Settings</h2>
              <p className="text-muted" style={{ fontSize: '0.875rem', marginTop: '4px' }}>
                Configure referral discounts and minimum pricing
              </p>
            </div>

            <form onSubmit={handlePricingSubmit}>
              {pricingError && <div className="alert alert-error">{pricingError}</div>}
              {pricingSuccess && <div className="alert alert-success">{pricingSuccess}</div>}

              <div className="pricing-section">
                <div className="grid grid-2">
                  <div className="form-group">
                    <label className="form-label" htmlFor="referral_discount">Discount Per Referral</label>
                    <div className="input-with-prefix">
                      <span className="input-prefix">$</span>
                      <input
                        type="number"
                        id="referral_discount"
                        name="referral_discount"
                        className="form-input"
                        value={pricing.referral_discount}
                        onChange={handlePricingChange}
                        step="0.01"
                        min="0"
                      />
                    </div>
                    <small className="text-muted">Amount discounted per completed referral in same service visit</small>
                  </div>
                  <div className="form-group">
                    <label className="form-label" htmlFor="minimum_price">Minimum Price Floor</label>
                    <div className="input-with-prefix">
                      <span className="input-prefix">$</span>
                      <input
                        type="number"
                        id="minimum_price"
                        name="minimum_price"
                        className="form-input"
                        value={pricing.minimum_price}
                        onChange={handlePricingChange}
                        step="0.01"
                        min="0"
                      />
                    </div>
                    <small className="text-muted">Price will never go below this amount</small>
                  </div>
                </div>
              </div>

              <button type="submit" className="btn btn-primary" disabled={pricingLoading}>
                {pricingLoading ? 'Saving...' : 'Save Pricing Settings'}
              </button>
            </form>
              </div>

              <div className="card mb-lg">
                <div className="card-header">
                  <h2 className="card-title">Pricing Tiers</h2>
              <p className="text-muted" style={{ fontSize: '0.875rem', marginTop: '4px' }}>
                Customize pricing tiers for your customers
              </p>
            </div>

            <form onSubmit={handlePricingTiersSubmit}>
              {pricingTiersError && <div className="alert alert-error">{pricingTiersError}</div>}
              {pricingTiersSuccess && <div className="alert alert-success">{pricingTiersSuccess}</div>}

              <div className="pricing-tiers-list">
                {pricingTiers.filter(t => t.is_active).map((tier, index) => (
                  <div key={tier.id} className="pricing-tier-item">
                    <div className="tier-row">
                      <div className="tier-field tier-field-name">
                        <label className="form-label">Tier Name</label>
                        <input
                          type="text"
                          className="form-input"
                          value={tier.name}
                          onChange={(e) => handlePricingTierChange(tier.id, 'name', e.target.value)}
                          placeholder="e.g., Small, Medium, Large"
                        />
                      </div>
                      <div className="tier-field tier-field-price">
                        <label className="form-label">Base Price</label>
                        <div className="input-with-prefix compact">
                          <span className="input-prefix">$</span>
                          <input
                            type="number"
                            className="form-input"
                            value={tier.default_price}
                            onChange={(e) => handlePricingTierChange(tier.id, 'default_price', e.target.value)}
                            step="0.01"
                            min="0"
                          />
                        </div>
                      </div>
                      <div className="tier-actions">
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-danger"
                          onClick={() => handleDeleteTier(tier)}
                          title="Delete tier"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Inactive tiers */}
              {pricingTiers.filter(t => !t.is_active).length > 0 && (
                <div className="inactive-tiers-section">
                  <h4 className="inactive-tiers-title">Inactive Tiers</h4>
                  <p className="text-muted" style={{ fontSize: '0.8rem', marginBottom: '12px' }}>
                    These tiers are deactivated but still referenced by existing customers.
                  </p>
                  {pricingTiers.filter(t => !t.is_active).map((tier) => (
                    <div key={tier.id} className="inactive-tier-item">
                      <span className="inactive-tier-name">{tier.name} - ${parseFloat(tier.default_price).toFixed(2)}</span>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline"
                        onClick={() => handleReactivateTier(tier)}
                      >
                        Reactivate
                      </button>
                    </div>
                  ))}
                </div>
              )}

              <div className="pricing-tiers-actions">
                <button type="button" className="btn btn-outline" onClick={handleAddTier}>
                  + Add Tier
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={pricingTiersLoading || !tiersModified}
                >
                  {pricingTiersLoading ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
              </div>

              <div className="card mb-lg">
                <div className="card-header">
                  <h2 className="card-title">Payment Methods</h2>
                </div>

            <form onSubmit={handlePaymentMethodsSubmit}>
              {paymentMethodsError && <div className="alert alert-error">{paymentMethodsError}</div>}
              {paymentMethodsSuccess && <div className="alert alert-success">{paymentMethodsSuccess}</div>}

              <p className="text-muted mb-md">Enable or disable payment methods shown in Service Mode and customer forms.</p>

              <div className="payment-methods-list">
                {paymentMethods.map((method) => (
                  <div key={method.id} className="payment-method-item">
                    <label className="payment-method-toggle">
                      <input
                        type="checkbox"
                        checked={method.enabled}
                        onChange={() => handlePaymentMethodToggle(method.id)}
                      />
                      <span className="payment-method-name">{method.name}</span>
                      {method.isBuiltIn && <span className="built-in-badge">Built-in</span>}
                    </label>
                    {!method.isBuiltIn && (
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-danger"
                        onClick={() => handleDeletePaymentMethod(method.id)}
                      >
                        Delete
                      </button>
                    )}
                  </div>
                ))}
              </div>

              <div className="add-payment-method">
                <input
                  type="text"
                  className="form-input"
                  placeholder="New payment method name"
                  value={newMethodName}
                  onChange={(e) => setNewMethodName(e.target.value)}
                />
                <button type="button" className="btn btn-outline" onClick={handleAddPaymentMethod}>
                  Add Method
                </button>
              </div>

              <button type="submit" className="btn btn-primary mt-md" disabled={paymentMethodsLoading}>
                {paymentMethodsLoading ? 'Saving...' : 'Save Payment Methods'}
              </button>
            </form>
              </div>
            </div>
          </div>
        )}

        {/* Invoicing Section */}
        {isAdmin() && (
          <div className={`settings-section ${expandedSections.invoicing ? 'expanded' : 'collapsed'}`}>
            <div className="settings-section-header" onClick={() => toggleSection('invoicing')}>
              <span className="section-arrow">{expandedSections.invoicing ? '\u25BC' : '\u25B6'}</span>
              <h2 className="section-title">Invoicing</h2>
            </div>
            <div className="settings-section-content">
              <div className="card mb-lg">
                <div className="card-header">
                  <h2 className="card-title">Invoice Defaults</h2>
                  <p className="text-muted" style={{ fontSize: '0.875rem', marginTop: '4px' }}>
                    Configure default title and message for Square invoices
                  </p>
                </div>

                <form onSubmit={handleInvoiceDefaultsSubmit}>
                  {invoiceDefaultsError && <div className="alert alert-error">{invoiceDefaultsError}</div>}
                  {invoiceDefaultsSuccess && <div className="alert alert-success">{invoiceDefaultsSuccess}</div>}

                  <div className="form-group">
                    <label className="form-label" htmlFor="defaultInvoiceTitle">Default Invoice Title</label>
                    <input
                      type="text"
                      id="defaultInvoiceTitle"
                      name="defaultInvoiceTitle"
                      className="form-input"
                      value={invoiceDefaults.defaultInvoiceTitle}
                      onChange={handleInvoiceDefaultsChange}
                      placeholder="{route_name}"
                    />
                    <small className="text-muted">Leave blank to use route name</small>
                  </div>

                  <div className="form-group">
                    <label className="form-label" htmlFor="defaultInvoiceMessage">Default Invoice Message</label>
                    <textarea
                      id="defaultInvoiceMessage"
                      name="defaultInvoiceMessage"
                      className="form-textarea"
                      value={invoiceDefaults.defaultInvoiceMessage}
                      onChange={handleInvoiceDefaultsChange}
                      rows={3}
                      placeholder="Thank you for your business!"
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Available Variables</label>
                    <div className="text-muted" style={{ fontSize: '0.875rem' }}>
                      <code>{'{route_name}'}</code> - Route name (e.g., "North Side")<br />
                      <code>{'{service_date}'}</code> - Date of service<br />
                      <code>{'{customer_name}'}</code> - Customer's full name
                    </div>
                  </div>

                  <div className="form-group" style={{ marginTop: '1.5rem', paddingTop: '1.5rem', borderTop: '1px solid var(--color-border)' }}>
                    <label className="checkbox-label" style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={invoiceDefaults.autoInvoiceSquare}
                        onChange={(e) => setInvoiceDefaults(prev => ({ ...prev, autoInvoiceSquare: e.target.checked }))}
                        style={{ marginTop: '3px' }}
                      />
                      <div>
                        <span style={{ fontWeight: 500 }}>Auto-send Square invoice on stop completion</span>
                        <p className="text-muted" style={{ fontSize: '0.875rem', marginTop: '4px', marginBottom: 0 }}>
                          When enabled, a Square invoice will automatically be sent when completing a stop with "Square" payment method (customer must have email on file)
                        </p>
                      </div>
                    </label>
                  </div>

                  <button type="submit" className="btn btn-primary" disabled={invoiceDefaultsLoading}>
                    {invoiceDefaultsLoading ? 'Saving...' : 'Save Invoice Defaults'}
                  </button>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Operations Section */}
        {isAdmin() && (
          <div className={`settings-section ${expandedSections.operations ? 'expanded' : 'collapsed'}`}>
            <div className="settings-section-header" onClick={() => toggleSection('operations')}>
              <span className="section-arrow">{expandedSections.operations ? '\u25BC' : '\u25B6'}</span>
              <h2 className="section-title">Operations</h2>
            </div>
            <div className="settings-section-content">
              <div className="card mb-lg">
                <div className="card-header">
                  <h2 className="card-title">Lead Sources</h2>
            </div>

            <form onSubmit={handleLeadSourcesSubmit}>
              {leadSourcesError && <div className="alert alert-error">{leadSourcesError}</div>}
              {leadSourcesSuccess && <div className="alert alert-success">{leadSourcesSuccess}</div>}

              <p className="text-muted mb-md">Configure "How did you find me?" options for tracking customer acquisition.</p>

              <div className="payment-methods-list">
                {leadSources.map((source) => (
                  <div key={source.id} className="payment-method-item">
                    <label className="payment-method-toggle">
                      <input
                        type="checkbox"
                        checked={source.enabled}
                        onChange={() => handleLeadSourceToggle(source.id)}
                      />
                      <span className="payment-method-name">{source.name}</span>
                      {source.isBuiltIn && <span className="built-in-badge">Built-in</span>}
                    </label>
                    {!source.isBuiltIn && (
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-danger"
                        onClick={() => handleDeleteLeadSource(source.id)}
                      >
                        Delete
                      </button>
                    )}
                  </div>
                ))}
              </div>

              <div className="add-payment-method">
                <input
                  type="text"
                  className="form-input"
                  placeholder="New lead source name"
                  value={newSourceName}
                  onChange={(e) => setNewSourceName(e.target.value)}
                />
                <button type="button" className="btn btn-outline" onClick={handleAddLeadSource}>
                  Add Source
                </button>
              </div>

              <button type="submit" className="btn btn-primary mt-md" disabled={leadSourcesLoading}>
                {leadSourcesLoading ? 'Saving...' : 'Save Lead Sources'}
              </button>
            </form>

            {/* Lead Source Stats */}
            <div className="lead-source-stats mt-lg">
              <div className="stats-header">
                <h3>Lead Source Analytics</h3>
                <select
                  className="form-select stats-period-select"
                  value={leadSourceStatsPeriod}
                  onChange={(e) => {
                    setLeadSourceStatsPeriod(e.target.value);
                    loadLeadSourceStats(e.target.value);
                  }}
                >
                  <option value="all">All Time</option>
                  <option value="season">This Season</option>
                  <option value="month">This Month</option>
                </select>
              </div>

              {leadSourceStatsLoading ? (
                <div className="stats-loading">Loading...</div>
              ) : leadSourceStats.stats.length === 0 ? (
                <div className="stats-empty">No lead source data yet</div>
              ) : (
                <div className="stats-list">
                  {leadSourceStats.stats.map((stat) => (
                    <div key={stat.id} className="stats-item">
                      <div className="stats-item-info">
                        <span className="stats-item-name">{stat.name}</span>
                        <span className="stats-item-count">{stat.count} ({stat.percentage}%)</span>
                      </div>
                      <div className="stats-bar-container">
                        <div
                          className="stats-bar"
                          style={{ width: `${stat.percentage}%` }}
                        />
                      </div>
                    </div>
                  ))}
                  <div className="stats-total">
                    Total: {leadSourceStats.total} customers with lead source
                  </div>
                </div>
              )}
            </div>
              </div>

              <div className="card mb-lg">
                <div className="card-header">
                  <h2 className="card-title">Expense Categories</h2>
                </div>

            <form onSubmit={handleExpenseCategoriesSubmit}>
              {expenseCategoriesError && <div className="alert alert-error">{expenseCategoriesError}</div>}
              {expenseCategoriesSuccess && <div className="alert alert-success">{expenseCategoriesSuccess}</div>}

              <p className="text-muted mb-md">Configure expense categories for tracking business costs. Disable categories you don't use.</p>

              <div className="payment-methods-list">
                {expenseCategories.map((category) => (
                  <div key={category.id} className="payment-method-item">
                    <label className="payment-method-toggle">
                      <input
                        type="checkbox"
                        checked={category.enabled}
                        onChange={() => handleExpenseCategoryToggle(category.id)}
                      />
                      <span className="payment-method-name">{category.name}</span>
                      {category.isBuiltIn && <span className="built-in-badge">Built-in</span>}
                    </label>
                    {!category.isBuiltIn && (
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-danger"
                        onClick={() => handleDeleteExpenseCategory(category.id)}
                      >
                        Delete
                      </button>
                    )}
                  </div>
                ))}
              </div>

              <div className="add-payment-method">
                <input
                  type="text"
                  className="form-input"
                  placeholder="New category name"
                  value={newCategoryName}
                  onChange={(e) => setNewCategoryName(e.target.value)}
                />
                <button type="button" className="btn btn-outline" onClick={handleAddExpenseCategory}>
                  Add Category
                </button>
              </div>

              <button type="submit" className="btn btn-primary mt-md" disabled={expenseCategoriesLoading}>
                {expenseCategoriesLoading ? 'Saving...' : 'Save Categories'}
              </button>
            </form>
              </div>

              {/* Service Add-ons Card */}
              <div className="card mb-lg">
                <div className="card-header">
                  <h2 className="card-title">Service Add-ons</h2>
                </div>

            <form onSubmit={handleServiceAddonsSubmit}>
              {serviceAddonsError && <div className="alert alert-error">{serviceAddonsError}</div>}
              {serviceAddonsSuccess && <div className="alert alert-success">{serviceAddonsSuccess}</div>}

              <p className="text-muted mb-md">Configure add-on services that can be added to stops (weed eating, mulching, etc.). These appear when completing a stop.</p>

              <div className="addon-tier-grid">
                {/* Header row */}
                <div className="addon-tier-header">
                  <div className="addon-col-toggle"></div>
                  <div className="addon-col-name">Add-on Name</div>
                  <div className="addon-col-price">Default $</div>
                  {pricingTiers.filter(t => t.is_active !== false).map(tier => (
                    <div key={tier.id} className="addon-col-tier">{tier.name}</div>
                  ))}
                  <div className="addon-col-actions"></div>
                </div>

                {/* Active add-on rows */}
                {serviceAddons.filter(a => a.enabled).map((addon) => (
                  <div key={addon.id} className="addon-tier-row">
                    <div className="addon-col-toggle">
                      <input
                        type="checkbox"
                        checked={addon.enabled}
                        onChange={() => handleServiceAddonToggle(addon.id)}
                      />
                    </div>
                    <div className="addon-col-name">
                      <input
                        type="text"
                        className="form-input"
                        value={addon.name}
                        onChange={(e) => handleAddonNameChange(addon.id, e.target.value)}
                        placeholder="Add-on name"
                      />
                    </div>
                    <div className="addon-col-price">
                      <div className="addon-price-input-wrapper">
                        <span className="price-prefix">$</span>
                        <input
                          type="number"
                          className="form-input addon-price-input"
                          value={addon.price}
                          onChange={(e) => handleAddonPriceChange(addon.id, e.target.value)}
                          step="0.01"
                          min="0"
                          placeholder="0.00"
                        />
                      </div>
                    </div>
                    {pricingTiers.filter(t => t.is_active !== false).map(tier => (
                      <div key={tier.id} className="addon-col-tier">
                        <div className="addon-price-input-wrapper">
                          <span className="price-prefix">$</span>
                          <input
                            type="number"
                            className="form-input addon-price-input"
                            value={addonTierPrices[addon.id]?.[tier.id] ?? ''}
                            onChange={(e) => handleAddonTierPriceChange(addon.id, tier.id, e.target.value)}
                            step="0.01"
                            min="0"
                            placeholder={addon.price.toFixed(2)}
                          />
                        </div>
                      </div>
                    ))}
                    <div className="addon-col-actions">
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-danger"
                        onClick={() => handleDeleteServiceAddon(addon.id)}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                ))}

                {/* Add new row */}
                <div className="addon-tier-row addon-tier-row-new">
                  <div className="addon-col-toggle"></div>
                  <div className="addon-col-name">
                    <input
                      type="text"
                      className="form-input"
                      placeholder="New add-on name"
                      value={newAddonName}
                      onChange={(e) => setNewAddonName(e.target.value)}
                    />
                  </div>
                  <div className="addon-col-price">
                    <div className="addon-price-input-wrapper">
                      <span className="price-prefix">$</span>
                      <input
                        type="number"
                        className="form-input addon-price-input"
                        placeholder="0.00"
                        value={newAddonPrice}
                        onChange={(e) => setNewAddonPrice(e.target.value)}
                        step="0.01"
                        min="0"
                      />
                    </div>
                  </div>
                  {pricingTiers.filter(t => t.is_active !== false).map(tier => (
                    <div key={tier.id} className="addon-col-tier">
                      <div className="addon-price-input-wrapper">
                        <span className="price-prefix">$</span>
                        <input
                          type="number"
                          className="form-input addon-price-input"
                          value={addonTierPrices['new']?.[tier.id] ?? ''}
                          onChange={(e) => handleAddonTierPriceChange('new', tier.id, e.target.value)}
                          step="0.01"
                          min="0"
                          placeholder="0.00"
                        />
                      </div>
                    </div>
                  ))}
                  <div className="addon-col-actions">
                    <button type="button" className="btn btn-outline" onClick={handleAddServiceAddon}>
                      Add
                    </button>
                  </div>
                </div>
              </div>

              {/* Inactive add-ons section */}
              {serviceAddons.filter(a => !a.enabled).length > 0 && (
                <div className="inactive-addons-section">
                  <p className="inactive-label">Inactive Add-ons</p>
                  {serviceAddons.filter(a => !a.enabled).map((addon) => (
                    <div key={addon.id} className="service-addon-item inactive">
                      <span style={{ flex: 1 }}>{addon.name}</span>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline"
                        onClick={() => handleServiceAddonToggle(addon.id)}
                      >
                        Reactivate
                      </button>
                    </div>
                  ))}
                </div>
              )}

              <button type="submit" className="btn btn-primary mt-md" disabled={serviceAddonsLoading}>
                {serviceAddonsLoading ? 'Saving...' : 'Save Add-ons'}
              </button>
            </form>
              </div>
            </div>
          </div>
        )}

        {/* Customer Intake Form Section */}
        {isAdmin() && companyCode && (
          <div className={`settings-section ${expandedSections.intake ? 'expanded' : 'collapsed'}`}>
            <div className="settings-section-header" onClick={() => toggleSection('intake')}>
              <span className="section-arrow">{expandedSections.intake ? '\u25BC' : '\u25B6'}</span>
              <h2 className="section-title">Customer Intake Form</h2>
            </div>
            <div className="settings-section-content">
              <div className="card mb-lg">
                <div className="card-header">
                  <h2 className="card-title">Intake Form URL</h2>
                </div>

                <p className="text-muted mb-md">
                  Share this link with potential customers to let them sign up directly.
                  New submissions will appear in your Customers list with a "New - From Form" badge.
                </p>

                {/* Primary Signup Link */}
                <div className="intake-link-section">
                  <label className="form-label">Your Signup Link:</label>
                  <div className="intake-link-row">
                    <input
                      type="text"
                      className="form-input intake-link-input"
                      value={`${window.location.origin}/new-customer/${customSlug || companyCode}`}
                      readOnly
                      onClick={(e) => e.target.select()}
                    />
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={() => {
                        navigator.clipboard.writeText(`${window.location.origin}/new-customer/${customSlug || companyCode}`);
                        setCopiedLink(true);
                        setTimeout(() => setCopiedLink(false), 2000);
                      }}
                    >
                      {copiedLink ? 'Copied!' : 'Copy Link'}
                    </button>
                  </div>
                  {customSlug && (
                    <small className="text-muted">
                      Original link still works: {window.location.origin}/new-customer/{companyCode}
                    </small>
                  )}
                </div>

                {/* Custom URL Section */}
                <div className="custom-slug-section">
                  <label className="form-label">Custom URL (optional)</label>
                  <p className="text-muted slug-description">
                    Create a memorable, branded URL for your signup form.
                  </p>

                  {slugError && <div className="alert alert-error slug-alert">{slugError}</div>}
                  {slugSuccess && <div className="alert alert-success slug-alert">{slugSuccess}</div>}

                  <div className="slug-input-row">
                    <span className="slug-prefix">{window.location.origin}/new-customer/</span>
                    <div className="slug-input-wrapper">
                      <input
                        type="text"
                        className={`form-input slug-input ${slugAvailable === true ? 'input-success' : ''} ${slugAvailable === false ? 'input-error' : ''}`}
                        value={customSlugInput}
                        onChange={handleSlugInputChange}
                        placeholder="your-company-name"
                        maxLength={50}
                      />
                      {slugChecking && <span className="slug-status checking">Checking...</span>}
                      {!slugChecking && slugAvailable === true && <span className="slug-status available">Available</span>}
                      {!slugChecking && slugAvailable === false && <span className="slug-status taken">Taken</span>}
                    </div>
                  </div>

                  <div className="slug-actions">
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={handleSlugSave}
                      disabled={slugSaving || slugChecking || slugAvailable === false}
                    >
                      {slugSaving ? 'Saving...' : 'Save Custom URL'}
                    </button>
                    {(customSlugInput || customSlug) && (
                      <button
                        type="button"
                        className="btn btn-outline"
                        onClick={handleClearSlug}
                        disabled={slugSaving}
                      >
                        Clear
                      </button>
                    )}
                  </div>

                  <small className="text-muted slug-help">
                    Must be 3-50 characters. Only lowercase letters, numbers, and hyphens allowed.
                  </small>
                </div>
              </div>

              <div className="card mb-lg">
                <div className="card-header">
                  <h2 className="card-title">Intake Form Disclaimer</h2>
                  <p className="text-muted" style={{ fontSize: '0.875rem', marginTop: '4px' }}>
                    Configure the payment disclaimer shown on the customer intake form
                  </p>
                </div>

                <form onSubmit={handleDisclaimerSettingsSubmit}>
                  {disclaimerSettingsError && <div className="alert alert-error">{disclaimerSettingsError}</div>}
                  {disclaimerSettingsSuccess && <div className="alert alert-success">{disclaimerSettingsSuccess}</div>}

                  <div className="form-group">
                    <label className="checkbox-label" style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        name="enabled"
                        checked={disclaimerSettings.enabled}
                        onChange={handleDisclaimerSettingsChange}
                        style={{ marginTop: '3px' }}
                      />
                      <div>
                        <span style={{ fontWeight: 500 }}>Show disclaimer on intake form</span>
                        <p className="text-muted" style={{ fontSize: '0.875rem', marginTop: '4px', marginBottom: 0 }}>
                          When enabled, customers must accept the disclaimer before submitting the intake form
                        </p>
                      </div>
                    </label>
                  </div>

                  {disclaimerSettings.enabled && (
                    <>
                      <div className="form-group">
                        <label className="form-label" htmlFor="disclaimerText">Disclaimer Text</label>
                        <textarea
                          id="disclaimerText"
                          name="text"
                          className="form-textarea"
                          value={disclaimerSettings.text}
                          onChange={handleDisclaimerSettingsChange}
                          rows={5}
                          placeholder="Enter your payment disclaimer text..."
                        />
                        <small className="text-muted">
                          Use <code>{'{company_name}'}</code> to insert your company name dynamically
                        </small>
                      </div>

                      <div className="disclaimer-preview">
                        <label className="form-label">Preview</label>
                        <div className="disclaimer-preview-box">
                          {disclaimerSettings.text.replace(/{company_name}/g, company.name || 'Your Company')}
                        </div>
                      </div>
                    </>
                  )}

                  <div className="disclaimer-actions">
                    <button type="submit" className="btn btn-primary" disabled={disclaimerSettingsLoading}>
                      {disclaimerSettingsLoading ? 'Saving...' : 'Save Disclaimer Settings'}
                    </button>
                    {disclaimerSettings.enabled && disclaimerSettings.text !== DEFAULT_DISCLAIMER && (
                      <button type="button" className="btn btn-outline" onClick={handleResetDisclaimer}>
                        Reset to Default
                      </button>
                    )}
                  </div>
                </form>
              </div>

              <div className="card mb-lg">
                <div className="card-header">
                  <h2 className="card-title">Route Optimization</h2>
                  <p className="text-muted" style={{ fontSize: '0.875rem', marginTop: '4px' }}>
                    Configure how routes are optimized in Service Mode
                  </p>
                </div>

                <form onSubmit={handleRouteSettingsSubmit}>
                  {routeSettingsError && <div className="alert alert-error">{routeSettingsError}</div>}
                  {routeSettingsSuccess && <div className="alert alert-success">{routeSettingsSuccess}</div>}

                  <div className="form-group">
                    <label className="checkbox-label" style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        name="useGps"
                        checked={routeSettings.useGps}
                        onChange={handleRouteSettingsChange}
                        style={{ marginTop: '3px' }}
                      />
                      <div>
                        <span style={{ fontWeight: 500 }}>Use my current GPS location when optimizing routes</span>
                        <p className="text-muted" style={{ fontSize: '0.875rem', marginTop: '4px', marginBottom: 0 }}>
                          When enabled, route optimization uses your live location as the starting point.
                          When disabled, uses the last completed stop as the starting point.
                        </p>
                      </div>
                    </label>
                  </div>

                  <button type="submit" className="btn btn-primary" disabled={routeSettingsLoading}>
                    {routeSettingsLoading ? 'Saving...' : 'Save Route Settings'}
                  </button>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Feedback Section */}
        <div className={`settings-section ${expandedSections.feedback ? 'expanded' : 'collapsed'}`}>
          <div className="settings-section-header" onClick={() => toggleSection('feedback')}>
            <span className="section-arrow">{expandedSections.feedback ? '\u25BC' : '\u25B6'}</span>
            <h2 className="section-title">Feedback</h2>
          </div>
          <div className="settings-section-content">
            <div className="card mb-lg feature-request-card">
              <div className="card-header">
                <h2 className="card-title">Suggest a Feature</h2>
                <p className="text-muted" style={{ fontSize: '0.875rem', marginTop: '4px' }}>
                  Have an idea to improve {getAppName()}? Let us know!
                </p>
              </div>

              <form onSubmit={handleFeatureRequestSubmit}>
                {featureRequestError && <div className="alert alert-error">{featureRequestError}</div>}
                {featureRequestSuccess && <div className="alert alert-success">{featureRequestSuccess}</div>}

                <div className="form-group">
                  <label className="form-label" htmlFor="featureSubject">Subject</label>
                  <input
                    type="text"
                    id="featureSubject"
                    name="subject"
                    className="form-input"
                    value={featureRequest.subject}
                    onChange={handleFeatureRequestChange}
                    placeholder="Brief summary of your idea"
                    maxLength={255}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label" htmlFor="featureDescription">Description</label>
                  <textarea
                    id="featureDescription"
                    name="description"
                    className="form-textarea"
                    value={featureRequest.description}
                    onChange={handleFeatureRequestChange}
                    rows={4}
                    placeholder="Describe your feature idea in detail..."
                  />
                </div>

                <button type="submit" className="btn btn-primary" disabled={featureRequestLoading}>
                  {featureRequestLoading ? 'Submitting...' : 'Submit Request'}
                </button>
              </form>

              {/* My Feature Requests */}
              {myFeatureRequests.length > 0 && (
                <div className="my-requests-section">
                  <h3 className="my-requests-title">My Feature Requests</h3>
                  {myFeatureRequestsLoading ? (
                    <div className="text-muted">Loading...</div>
                  ) : (
                    <div className="my-requests-list">
                      {myFeatureRequests.map(request => (
                        <div key={request.id} className="my-request-card">
                          <div className="my-request-header">
                            <span className="my-request-subject">{request.subject}</span>
                            <span className={`my-request-status status-${request.status}`}>
                              {request.status === 'new' && 'New'}
                              {request.status === 'under_review' && 'Under Review'}
                              {request.status === 'planned' && 'Planned'}
                              {request.status === 'in_progress' && 'In Progress'}
                              {request.status === 'completed' && 'Completed'}
                              {request.status === 'declined' && 'Declined'}
                            </span>
                          </div>
                          <div className="my-request-date">
                            Submitted: {new Date(request.created_at).toLocaleDateString('en-US', {
                              month: 'short', day: 'numeric', year: 'numeric'
                            })}
                          </div>
                          <div className="my-request-description">
                            Your request: "{request.description}"
                          </div>
                          {request.admin_notes ? (
                            <div className="my-request-response">
                              <strong>Admin response:</strong> "{request.admin_notes}"
                            </div>
                          ) : (
                            <div className="my-request-awaiting">(Awaiting review)</div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .app-header-content {
          overflow: hidden;
        }
        .header-nav {
          display: flex;
          align-items: center;
          gap: 4px;
          flex-shrink: 1;
          overflow: hidden;
        }
        .nav-link {
          background: transparent;
          border: none;
          color: rgba(255,255,255,0.8);
          padding: 8px 12px;
          border-radius: 6px;
          cursor: pointer;
          font-size: 0.875rem;
          transition: all 0.15s ease;
          white-space: nowrap;
          flex-shrink: 0;
        }
        .nav-link:hover {
          background: rgba(255,255,255,0.1);
          color: white;
        }
        .nav-link.active {
          background: rgba(255,255,255,0.2);
          color: white;
        }
        .storm-link {
          background: var(--color-secondary) !important;
          color: white !important;
        }
        .storm-link:hover {
          background: var(--color-secondary-dark) !important;
        }
        .admin-link {
          background: linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%) !important;
          color: white !important;
        }
        .admin-link:hover {
          background: linear-gradient(135deg, #6d28d9 0%, #5b21b6 100%) !important;
        }
        .logout-link {
          margin-left: 8px;
          border-left: 1px solid rgba(255,255,255,0.2);
          padding-left: 16px;
        }
        /* Mobile Menu */
        .mobile-menu-btn {
          display: none;
          flex-direction: column;
          justify-content: space-between;
          width: 24px;
          height: 18px;
          background: transparent;
          border: none;
          cursor: pointer;
          padding: 0;
          flex-shrink: 0;
        }
        .mobile-menu-btn span {
          display: block;
          width: 100%;
          height: 2px;
          background: white;
          border-radius: 1px;
        }
        .mobile-nav {
          display: flex;
          flex-direction: column;
          padding: 8px 16px 16px;
          border-top: 1px solid rgba(255,255,255,0.1);
        }
        .mobile-nav-link {
          background: transparent;
          border: none;
          color: rgba(255,255,255,0.8);
          padding: 12px 8px;
          text-align: left;
          font-size: 1rem;
          cursor: pointer;
          border-radius: 6px;
          transition: all 0.15s ease;
        }
        .mobile-nav-link:hover, .mobile-nav-link.active {
          background: rgba(255,255,255,0.1);
          color: white;
        }
        .mobile-nav-link.storm-link {
          background: var(--color-secondary);
          color: white;
          margin-top: 8px;
        }
        .mobile-nav-link.admin-link {
          background: linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%);
          color: white;
          margin-top: 8px;
        }
        .mobile-nav-link.logout-link {
          margin-top: 8px;
          border-top: 1px solid rgba(255,255,255,0.1);
          padding-top: 16px;
        }
        @media (max-width: 900px) {
          .desktop-nav {
            display: none;
          }
          .mobile-menu-btn {
            display: flex;
          }
        }
        .subscription-info {
          display: flex;
          align-items: center;
          padding: 8px 0;
        }
        /* Account Banner Styles */
        .account-banner {
          background: linear-gradient(135deg, #4CAF50 0%, #388E3C 100%);
          border-radius: 12px;
          padding: 16px 20px;
          margin-bottom: 24px;
          color: white;
          box-shadow: 0 4px 12px rgba(76, 175, 80, 0.2);
        }
        .account-banner-name {
          font-size: 1.25rem;
          font-weight: 600;
          display: flex;
          align-items: center;
          gap: 10px;
          margin-bottom: 8px;
        }
        .account-icon {
          font-size: 1.1rem;
        }
        .account-banner-details {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 0.9rem;
          opacity: 0.9;
        }
        .account-detail {
          display: flex;
          align-items: center;
          gap: 6px;
        }
        .detail-label {
          opacity: 0.8;
        }
        .detail-value {
          font-weight: 500;
        }
        .account-divider {
          opacity: 0.5;
        }
        @media (max-width: 480px) {
          .account-banner-details {
            flex-direction: column;
            align-items: flex-start;
            gap: 4px;
          }
          .account-divider {
            display: none;
          }
        }
        .pricing-section {
          margin-bottom: 24px;
        }
        .pricing-section-title {
          font-size: 1rem;
          font-weight: 600;
          color: var(--color-text);
          margin: 0 0 12px 0;
        }
        .pricing-table {
          border: 1px solid var(--color-border);
          border-radius: 8px;
          overflow: hidden;
        }
        .pricing-row {
          display: grid;
          grid-template-columns: 1fr 1fr 1fr;
          gap: 12px;
          padding: 12px 16px;
          border-bottom: 1px solid var(--color-border);
        }
        .pricing-row:last-child {
          border-bottom: none;
        }
        .pricing-row.pricing-header {
          background: var(--color-background);
          font-weight: 600;
          font-size: 0.875rem;
          color: var(--color-text-light);
        }
        .pricing-cell {
          display: flex;
          align-items: center;
        }
        .pricing-cell.tier-label {
          font-weight: 500;
        }
        .input-with-prefix {
          display: flex;
          align-items: center;
        }
        .input-with-prefix.compact {
          max-width: 120px;
        }
        .input-with-prefix .input-prefix {
          background: var(--color-background);
          padding: 0 8px;
          height: 36px;
          display: flex;
          align-items: center;
          border: 1px solid var(--color-border);
          border-right: none;
          border-radius: 6px 0 0 6px;
          color: var(--color-text-light);
          font-size: 0.875rem;
        }
        .input-with-prefix .form-input {
          border-radius: 0 6px 6px 0;
          height: 36px;
          padding: 0 8px;
        }
        @media (max-width: 768px) {
          .grid-2 {
            grid-template-columns: 1fr;
          }
          .pricing-row {
            grid-template-columns: 1fr;
            gap: 8px;
          }
          .pricing-row.pricing-header {
            display: none;
          }
          .pricing-cell.tier-label {
            font-weight: 600;
            margin-bottom: 4px;
          }
          .pricing-cell::before {
            content: attr(data-label);
            font-size: 0.75rem;
            color: var(--color-text-light);
            margin-right: 8px;
          }
        }

        /* Payment Methods */
        .payment-methods-list {
          display: flex;
          flex-direction: column;
          gap: 12px;
          margin-bottom: 20px;
        }
        .payment-method-item {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 12px 16px;
          background: #f9fafb;
          border-radius: 8px;
        }
        .payment-method-toggle {
          display: flex;
          align-items: center;
          gap: 12px;
          cursor: pointer;
        }
        .payment-method-toggle input[type="checkbox"] {
          width: 20px;
          height: 20px;
          cursor: pointer;
        }
        .payment-method-name {
          font-weight: 500;
        }
        .built-in-badge {
          font-size: 0.7rem;
          background: #e5e7eb;
          color: #6b7280;
          padding: 2px 6px;
          border-radius: 4px;
          margin-left: 8px;
        }
        .btn-outline-danger {
          color: #ef4444;
          border-color: #ef4444;
          background: transparent;
        }
        .btn-outline-danger:hover {
          background: #fef2f2;
        }
        .add-payment-method {
          display: flex;
          gap: 12px;
          margin-bottom: 16px;
        }
        .add-payment-method .form-input {
          flex: 1;
          max-width: 300px;
        }

        /* Service Add-ons */
        .service-addons-list {
          display: flex;
          flex-direction: column;
          gap: 12px;
          margin-bottom: 16px;
        }
        .service-addon-item {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 12px;
          background: #f9fafb;
          border-radius: 8px;
        }
        .service-addon-item.inactive {
          opacity: 0.6;
          background: #f3f4f6;
        }
        .addon-toggle {
          display: flex;
          align-items: center;
        }
        .addon-toggle input[type="checkbox"] {
          width: 20px;
          height: 20px;
          cursor: pointer;
        }
        .addon-name-input {
          flex: 1;
          min-width: 150px;
        }
        .addon-price-input-wrapper {
          display: flex;
          align-items: center;
          gap: 4px;
        }
        .addon-price-input-wrapper .price-prefix {
          color: #6b7280;
          font-weight: 500;
        }
        .addon-price-input {
          width: 80px;
        }
        .inactive-addons-section {
          margin-top: 16px;
          padding-top: 16px;
          border-top: 1px dashed #d1d5db;
        }
        .inactive-label {
          font-size: 0.85rem;
          color: #6b7280;
          font-weight: 500;
          margin-bottom: 12px;
        }
        .add-addon-row {
          display: flex;
          gap: 12px;
          align-items: center;
          margin-bottom: 16px;
        }
        .add-addon-row .form-input:first-child {
          flex: 1;
          max-width: 250px;
        }

        /* Addon Tier Grid */
        .addon-tier-grid { overflow-x: auto; margin-bottom: 16px; }
        .addon-tier-header, .addon-tier-row { display: flex; align-items: center; gap: 8px; padding: 8px 0; min-width: fit-content; }
        .addon-tier-header { font-weight: 600; font-size: 0.85rem; color: #64748b; border-bottom: 2px solid #e2e8f0; padding-bottom: 12px; margin-bottom: 4px; }
        .addon-tier-row { border-bottom: 1px solid #f1f5f9; }
        .addon-tier-row-new { border-bottom: none; padding-top: 12px; border-top: 2px dashed #e2e8f0; }
        .addon-col-toggle { width: 32px; flex-shrink: 0; }
        .addon-col-name { width: 160px; flex-shrink: 0; }
        .addon-col-name .form-input { width: 100%; }
        .addon-col-price { width: 100px; flex-shrink: 0; }
        .addon-col-tier { width: 100px; flex-shrink: 0; }
        .addon-col-actions { width: 70px; flex-shrink: 0; }
        .addon-col-tier .addon-price-input, .addon-col-price .addon-price-input { width: 80px; }
        @media (max-width: 768px) {
          .addon-tier-grid { font-size: 0.85rem; }
          .addon-col-name { width: 120px; }
          .addon-col-tier { width: 85px; }
          .addon-col-price { width: 85px; }
        }

        /* Intake Form Link */
        .intake-link-section {
          margin-top: 16px;
        }
        .intake-link-row {
          display: flex;
          gap: 12px;
          margin-bottom: 8px;
        }
        .intake-link-input {
          flex: 1;
          font-family: monospace;
          font-size: 0.875rem;
          background: #f9fafb;
        }
        .intake-link-row .btn {
          white-space: nowrap;
        }

        /* Custom Slug Section */
        .custom-slug-section {
          margin-top: 24px;
          padding-top: 24px;
          border-top: 1px solid #e5e7eb;
        }
        .slug-description {
          font-size: 0.875rem;
          margin-bottom: 12px;
        }
        .slug-alert {
          margin-bottom: 12px;
          padding: 8px 12px;
          font-size: 0.875rem;
        }
        .slug-input-row {
          display: flex;
          align-items: center;
          gap: 0;
          flex-wrap: wrap;
          margin-bottom: 12px;
        }
        .slug-prefix {
          background: #f3f4f6;
          padding: 8px 12px;
          border: 1px solid #d1d5db;
          border-right: none;
          border-radius: 6px 0 0 6px;
          font-size: 0.875rem;
          color: #6b7280;
          white-space: nowrap;
        }
        .slug-input-wrapper {
          flex: 1;
          min-width: 150px;
          position: relative;
          display: flex;
          align-items: center;
        }
        .slug-input {
          border-radius: 0 6px 6px 0 !important;
          font-family: monospace;
          width: 100%;
        }
        .slug-input.input-success {
          border-color: #10b981;
        }
        .slug-input.input-error {
          border-color: #ef4444;
        }
        .slug-status {
          position: absolute;
          right: 10px;
          font-size: 0.75rem;
          font-weight: 500;
        }
        .slug-status.checking {
          color: #6b7280;
        }
        .slug-status.available {
          color: #10b981;
        }
        .slug-status.taken {
          color: #ef4444;
        }
        .slug-actions {
          display: flex;
          gap: 12px;
          margin-bottom: 12px;
        }
        .slug-help {
          display: block;
          font-size: 0.8rem;
        }
        @media (max-width: 600px) {
          .slug-input-row {
            flex-direction: column;
            align-items: stretch;
          }
          .slug-prefix {
            border-radius: 6px 6px 0 0;
            border-right: 1px solid #d1d5db;
            border-bottom: none;
            text-align: center;
          }
          .slug-input {
            border-radius: 0 0 6px 6px !important;
          }
        }

        /* Lead Source Stats */
        .lead-source-stats {
          border-top: 1px solid #e5e7eb;
          padding-top: 20px;
        }
        .stats-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 16px;
        }
        .stats-header h3 {
          margin: 0;
          font-size: 1rem;
          font-weight: 600;
          color: #374151;
        }
        .stats-period-select {
          width: auto;
          padding: 6px 12px;
          font-size: 0.875rem;
        }
        .stats-loading, .stats-empty {
          text-align: center;
          color: #6b7280;
          padding: 20px;
          font-size: 0.9rem;
        }
        .stats-list {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }
        .stats-item {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }
        .stats-item-info {
          display: flex;
          justify-content: space-between;
          align-items: center;
        }
        .stats-item-name {
          font-size: 0.9rem;
          color: #374151;
        }
        .stats-item-count {
          font-size: 0.85rem;
          color: #6b7280;
          font-weight: 500;
        }
        .stats-bar-container {
          height: 8px;
          background: #e5e7eb;
          border-radius: 4px;
          overflow: hidden;
        }
        .stats-bar {
          height: 100%;
          background: linear-gradient(90deg, #3b82f6, #8b5cf6);
          border-radius: 4px;
          transition: width 0.3s ease;
        }
        .stats-total {
          margin-top: 8px;
          padding-top: 12px;
          border-top: 1px solid #e5e7eb;
          font-size: 0.85rem;
          color: #6b7280;
          text-align: right;
        }

        /* Pricing Tiers */
        .pricing-tiers-list {
          display: flex;
          flex-direction: column;
          gap: 16px;
          margin-bottom: 20px;
        }
        .pricing-tier-item {
          padding: 16px;
          background: #f9fafb;
          border-radius: 8px;
          border: 1px solid #e5e7eb;
        }
        .tier-row {
          display: flex;
          gap: 12px;
          align-items: flex-end;
        }
        .tier-field {
          flex: 1;
        }
        .tier-field-name {
          flex: 2;
        }
        .tier-field-price {
          flex: 1;
          min-width: 100px;
        }
        .tier-field .form-label {
          margin-bottom: 4px;
          font-size: 0.8rem;
          color: #6b7280;
        }
        .tier-actions {
          display: flex;
          align-items: center;
          padding-bottom: 2px;
          flex-shrink: 0;
        }
        .pricing-tiers-actions {
          display: flex;
          gap: 12px;
          margin-top: 16px;
        }
        .inactive-tiers-section {
          margin-top: 20px;
          padding: 16px;
          background: #fef3c7;
          border-radius: 8px;
          border: 1px solid #fcd34d;
        }
        .inactive-tiers-title {
          margin: 0 0 8px 0;
          font-size: 0.9rem;
          color: #92400e;
        }
        .inactive-tier-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 8px 0;
          border-bottom: 1px solid #fcd34d;
        }
        .inactive-tier-item:last-child {
          border-bottom: none;
        }
        .inactive-tier-name {
          color: #92400e;
          font-size: 0.9rem;
        }
        @media (max-width: 600px) {
          .tier-row {
            flex-direction: column;
            align-items: stretch;
            gap: 12px;
          }
          .tier-actions {
            justify-content: flex-end;
          }
          .pricing-tiers-actions {
            flex-direction: column;
          }
        }

        .mt-md {
          margin-top: 16px;
        }
        .mb-md {
          margin-bottom: 16px;
        }

        /* Feature Request */
        .feature-request-card {
          background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
          border: 1px solid #bae6fd;
        }
        .feature-request-card .card-title {
          color: #0369a1;
        }
        .feature-request-card .form-textarea {
          min-height: 100px;
          resize: vertical;
        }

        /* My Feature Requests */
        .my-requests-section {
          margin-top: 24px;
          padding-top: 24px;
          border-top: 1px solid #bae6fd;
        }
        .my-requests-title {
          font-size: 1rem;
          font-weight: 600;
          color: #0369a1;
          margin: 0 0 16px 0;
        }
        .my-requests-list {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }
        .my-request-card {
          background: white;
          border: 1px solid #e5e7eb;
          border-radius: 8px;
          padding: 12px 16px;
        }
        .my-request-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 6px;
        }
        .my-request-subject {
          font-weight: 600;
          color: #1f2937;
        }
        .my-request-status {
          font-size: 0.75rem;
          font-weight: 600;
          padding: 3px 10px;
          border-radius: 12px;
        }
        .my-request-status.status-new {
          background: #dbeafe;
          color: #1e40af;
        }
        .my-request-status.status-under_review {
          background: #fef3c7;
          color: #92400e;
        }
        .my-request-status.status-planned {
          background: #ede9fe;
          color: #6d28d9;
        }
        .my-request-status.status-in_progress {
          background: #ffedd5;
          color: #c2410c;
        }
        .my-request-status.status-completed {
          background: #d1fae5;
          color: #065f46;
        }
        .my-request-status.status-declined {
          background: #f3f4f6;
          color: #6b7280;
        }
        .my-request-date {
          font-size: 0.8rem;
          color: #6b7280;
          margin-bottom: 8px;
        }
        .my-request-description {
          font-size: 0.875rem;
          color: #4b5563;
          margin-bottom: 8px;
          font-style: italic;
        }
        .my-request-response {
          font-size: 0.875rem;
          background: #f0fdf4;
          border: 1px solid #bbf7d0;
          border-radius: 6px;
          padding: 8px 12px;
          color: #166534;
        }
        .my-request-awaiting {
          font-size: 0.8rem;
          color: #9ca3af;
          font-style: italic;
        }
        /* Radio button styles */
        .radio-label {
          display: flex;
          align-items: center;
          gap: 10px;
          cursor: pointer;
          padding: 8px 0;
          user-select: none;
        }
        .radio-label input[type="radio"] {
          width: 18px;
          height: 18px;
          accent-color: var(--color-primary);
          cursor: pointer;
        }
        .radio-text {
          font-size: 0.95rem;
          color: var(--color-text);
        }
        /* Disclaimer Settings */
        .disclaimer-preview {
          margin-top: 16px;
          margin-bottom: 16px;
        }
        .disclaimer-preview-box {
          background: #f9fafb;
          border: 1px solid #e5e7eb;
          border-radius: 8px;
          padding: 16px;
          font-size: 0.875rem;
          color: #4b5563;
          line-height: 1.5;
        }
        .disclaimer-actions {
          display: flex;
          gap: 12px;
          margin-top: 16px;
        }

        /* Collapsible Sections */
        .settings-section {
          margin-bottom: 24px;
          background: white;
          border-radius: 12px;
          box-shadow: 0 1px 3px rgba(0,0,0,0.1);
          overflow: hidden;
        }
        .settings-section-header {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 16px 20px;
          background: linear-gradient(135deg, var(--color-primary) 0%, var(--color-primary-dark) 100%);
          color: #ffffff;
          cursor: pointer;
          user-select: none;
          transition: background 0.2s ease;
        }
        .settings-section-header:hover {
          background: linear-gradient(135deg, var(--color-primary-dark) 0%, var(--color-primary) 100%);
        }
        .section-arrow {
          font-size: 0.875rem;
          width: 16px;
          transition: transform 0.2s ease;
          color: #ffffff;
        }
        .section-title {
          font-size: 1.125rem;
          font-weight: 600;
          margin: 0;
          color: #ffffff;
        }
        .settings-section-content {
          max-height: 0;
          overflow: hidden;
          transition: max-height 0.3s ease-out;
        }
        .settings-section.expanded .settings-section-content {
          max-height: 5000px;
          transition: max-height 0.5s ease-in;
        }
        .settings-section-content > .card {
          border-radius: 0;
          box-shadow: none;
          border-bottom: 1px solid var(--color-border);
        }
        .settings-section-content > .card:last-child {
          border-bottom: none;
        }
        .settings-section-content > .card.mb-lg {
          margin-bottom: 0 !important;
        }
        @media (max-width: 768px) {
          .settings-section-header {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 16px 20px;
          background: linear-gradient(135deg, var(--color-primary) 0%, var(--color-primary-dark) 100%);
          color: #ffffff;
          cursor: pointer;
          user-select: none;
          transition: background 0.2s ease;
        }
          .section-title {
            font-size: 1rem;
          }
        }

        /* Team Management Styles */
        .team-list {
          display: flex;
          flex-direction: column;
        }

        .team-member-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 16px 20px;
          border-bottom: 1px solid #e5e7eb;
        }

        .team-member-row:last-child {
          border-bottom: none;
        }

        .team-member-info {
          flex: 1;
        }

        .team-member-name {
          font-weight: 500;
          color: #1e293b;
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .you-badge {
          font-size: 0.75rem;
          color: #64748b;
          font-weight: 400;
        }

        .team-member-email {
          font-size: 0.875rem;
          color: #64748b;
          margin-top: 2px;
        }

        .team-member-actions {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .role-select {
          width: auto;
          padding: 6px 12px;
          font-size: 0.875rem;
        }

        .confirm-remove {
          display: flex;
          align-items: center;
          gap: 6px;
          font-size: 0.875rem;
        }

        .confirm-remove span {
          color: #dc2626;
          font-weight: 500;
        }

        /* Role Badges */
        .badge-owner {
          background: #f3e8ff;
          color: #7c3aed;
        }

        .badge-admin {
          background: #dbeafe;
          color: #1d4ed8;
        }

        .badge-crew-lead {
          background: #dcfce7;
          color: #15803d;
        }

        .badge-driver {
          background: #f3f4f6;
          color: #4b5563;
        }

        /* Pending Invitations */
        .pending-invitations {
          border-top: 2px solid #e5e7eb;
          padding-top: 16px;
          margin-top: 8px;
        }

        .subsection-title {
          font-size: 0.875rem;
          font-weight: 600;
          color: #64748b;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          padding: 0 20px;
          margin-bottom: 8px;
        }

        .invitation-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 12px 20px;
          background: #fafafa;
        }

        .invitation-row:nth-child(odd) {
          background: #f5f5f5;
        }

        .invitation-email {
          font-weight: 500;
          color: #1e293b;
        }

        .invitation-meta {
          display: flex;
          align-items: center;
          gap: 12px;
          margin-top: 4px;
        }

        .invitation-expires {
          font-size: 0.75rem;
          color: #94a3b8;
        }

        /* Modal Styles */
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          padding: 20px;
        }

        .modal {
          background: white;
          border-radius: 12px;
          width: 100%;
          max-width: 480px;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
        }

        .modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 20px 24px;
          border-bottom: 1px solid #e5e7eb;
        }

        .modal-header h2 {
          font-size: 1.25rem;
          font-weight: 600;
          color: #1e293b;
          margin: 0;
        }

        .modal-close {
          background: none;
          border: none;
          font-size: 1.5rem;
          color: #64748b;
          cursor: pointer;
          padding: 4px 8px;
          line-height: 1;
        }

        .modal-close:hover {
          color: #1e293b;
        }

        .modal form {
          padding: 24px;
        }

        .modal-footer {
          display: flex;
          justify-content: flex-end;
          gap: 12px;
          padding-top: 16px;
          margin-top: 8px;
          border-top: 1px solid #e5e7eb;
        }

        /* Button variations */
        .btn-sm {
          padding: 6px 12px;
          font-size: 0.8125rem;
        }

        .btn-error {
          background: #dc2626;
          color: white;
          border: none;
        }

        .btn-error:hover {
          background: #b91c1c;
        }

        @media (max-width: 640px) {
          .team-member-row {
            flex-direction: column;
            align-items: flex-start;
            gap: 12px;
          }

          .team-member-actions {
            width: 100%;
            justify-content: flex-start;
          }

          .invitation-row {
            flex-direction: column;
            align-items: flex-start;
            gap: 8px;
          }
        }
      `}</style>
    </div>
  );
}

export default Settings;
